import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  ShieldCheck,
  FileSpreadsheet,
  Download,
  Trash2,
  Search,
  RefreshCw,
  Plus,
  Users,
  Clock,
  CheckCircle2,
  Sparkles,
  TrendingUp,
  CreditCard,
  Phone,
  MapPin,
  Calendar,
  Layers,
  Edit2,
  RotateCcw,
  Check,
  IndianRupee,
  Gift,
  FolderPlus,
  Crown,
  Eye,
  EyeOff,
  PhoneCall,
  MessageSquare,
  Filter,
  ChevronDown,
  ChevronUp,
  Navigation,
  Star,
  CalendarClock
} from 'lucide-react';
import {
  exportBookingToExcel,
  exportMasterBookingsToExcel,
  BookingRecord,
  ServiceStatus,
  PaymentStatus,
  BookingSource
} from '../utils/excelExport';
import {
  getAdminBookings,
  updateAdminBooking,
  deleteAdminBooking,
  assignStaffToBooking,
  createAdminManualBooking
} from '../services/bookingsService';
import {
  StaffPartner,
  fetchStaffPartnersFromRemote,
  saveStaffPartnerRemote,
  deleteStaffPartnerRemote,
  toggleStaffDutyRemote
} from '../services/partnerService';
import {
  ServiceItem,
  BundleItem,
  CategoryItem,
  getServicesList,
  saveService,
  deleteService,
  updateServicePrice,
  getBundlesList,
  getCategoriesList,
  saveCategoryRemote,
  deleteCategoryRemote,
  saveBundleRemote,
  deleteBundleRemote,
  syncAllCatalogFromRemote,
  resetCatalogToDefaults,
  subscribeToCatalogUpdates,
  syncServicesFromRemote,
  syncCategoriesFromRemote,
  syncBundlesFromRemote
} from '../services/catalogService';
import {
  BhilaiLocationItem,
  getOperationalAreas,
  saveOperationalAreaRemote,
  deleteOperationalAreaRemote,
  toggleAreaServiceableRemote,
  resetOperationalAreasRemote,
  syncOperationalAreasFromRemote,
  HARI_RAJ_HUB,
  calculateHaversineDistance,
  detectLiveGeolocation
} from '../services/locationService';
import {
  getReviewsList,
  syncReviewsFromRemote,
  submitReview,
  deleteReview,
  CustomerReview,
  REVIEWS_UPDATED_EVENT
} from '../services/reviewService';
import { formatDuration } from '../data/services';
import { usePWAInstall } from '../utils/pwaInstall';
import { AdminInstallGuideModal } from './AdminInstallGuideModal';
import { RescheduleBookingModal } from './RescheduleBookingModal';

const InstagramIcon: React.FC<{ size?: number; style?: React.CSSProperties; className?: string }> = ({ size = 14, style, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style} className={className}>
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
  </svg>
);

const WhatsAppIcon: React.FC<{ size?: number; style?: React.CSSProperties; className?: string }> = ({ size = 14, style, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style} className={className}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

interface AdminBookingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  adminPin?: string;
  adminRole?: 'super_admin' | 'admin';
  onSwitchRole?: (role: 'super_admin' | 'admin') => void;
  onLockOut?: () => void;
}

export const AdminBookingsModal: React.FC<AdminBookingsModalProps> = ({
  isOpen,
  onClose,
  adminPin = '7994',
  adminRole = 'super_admin',
  onSwitchRole,
  onLockOut
}) => {
  const [currentRole, setCurrentRole] = useState<'super_admin' | 'admin'>(adminRole);
  const [isUnlockSuperModalOpen, setIsUnlockSuperModalOpen] = useState(false);
  const [superPinInput, setSuperPinInput] = useState('');
  const [superPinError, setSuperPinError] = useState('');
  const [showSuperPin, setShowSuperPin] = useState(false);

  // PWA Admin App Install State
  const { isStandalone } = usePWAInstall();
  const [showAdminInstallModal, setShowAdminInstallModal] = useState(false);

  useEffect(() => {
    if (isOpen && typeof window !== 'undefined' && window.location.search.includes('install=true')) {
      setShowAdminInstallModal(true);
      try {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('install');
        window.history.replaceState(
          window.history.state,
          '',
          cleanUrl.pathname + (cleanUrl.search ? cleanUrl.search : '') + cleanUrl.hash
        );
      } catch {}
    }
  }, [isOpen]);

  const handleOpenInstallModal = () => {
    const isDirectAdmin =
      typeof window !== 'undefined' &&
      ((window as any).__INITIAL_HTTP_PATHNAME__ || window.location.pathname.toLowerCase()).includes(
        currentRole === 'super_admin' ? '/super-admin' : '/admin'
      );

    if (!isDirectAdmin) {
      try {
        sessionStorage.setItem('blishka_admin_auth', 'true');
        sessionStorage.setItem('blishka_admin_pin', adminPin);
        sessionStorage.setItem('blishka_admin_role', currentRole);
        const portalKey = currentRole === 'super_admin' ? 'super-admin' : 'admin';
        localStorage.setItem('blishka_installed_portal', portalKey);
        document.cookie = `blishka_installed_portal=${portalKey}; path=/; max-age=31536000; SameSite=Lax`;
      } catch {}
      const target = currentRole === 'super_admin' ? '/super-admin' : '/admin';
      window.location.href = `${target}?source=pwa&app=admin&install=true`;
      return;
    }

    setShowAdminInstallModal(true);
  };

  const SUPER_ADMIN_PIN = (import.meta.env.VITE_SUPER_ADMIN_PIN || '7994').trim();

  useEffect(() => {
    setCurrentRole(adminRole);
  }, [adminRole]);

  useEffect(() => {
    if (isOpen) {
      localStorage.setItem('blishka_installed_portal', currentRole === 'super_admin' ? 'super-admin' : 'admin');
    }
  }, [isOpen, currentRole]);

  const [adminTab, setAdminTab] = useState<'dispatch' | 'fleet' | 'finance' | 'catalog' | 'areas' | 'reviews'>('dispatch');

  useEffect(() => {
    if (currentRole === 'admin' && (adminTab === 'finance' || adminTab === 'areas' || adminTab === 'reviews')) {
      setAdminTab('dispatch');
    }
  }, [currentRole, adminTab]);

  const handleUnlockSuperAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    if (superPinInput.trim() === SUPER_ADMIN_PIN) {
      setCurrentRole('super_admin');
      if (onSwitchRole) onSwitchRole('super_admin');
      sessionStorage.setItem('blishka_admin_role', 'super_admin');
      sessionStorage.setItem('blishka_admin_pin', superPinInput.trim());
      setIsUnlockSuperModalOpen(false);
      setSuperPinInput('');
      setSuperPinError('');
      showToast('Unlocked Super Admin Financial Console');
    } else {
      setSuperPinError('Invalid PIN code. Super Admin PIN required (7994).');
      setSuperPinInput('');
    }
  };
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [channelFilter, setChannelFilter] = useState<'all' | 'call' | 'instagram' | 'whatsapp' | 'web'>('all');
  const [isD1Connected, setIsD1Connected] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [adminRescheduleBooking, setAdminRescheduleBooking] = useState<BookingRecord | null>(null);

  // Dynamic Mobile Responsiveness & Scroll Collapsing State
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const [showKpiMobile, setShowKpiMobile] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const lastScrollY = useRef(0);

  const handleBodyScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const currentScrollY = e.currentTarget.scrollTop;
    if (currentScrollY > 40 && currentScrollY > lastScrollY.current) {
      // User scrolled down past threshold -> collapse KPI counts & filter trays on mobile
      if (!isScrolledDown) setIsScrolledDown(true);
    } else if (currentScrollY < lastScrollY.current - 12 || currentScrollY <= 15) {
      // User scrolled up or reached the top -> reveal
      if (isScrolledDown) setIsScrolledDown(false);
    }
    lastScrollY.current = currentScrollY;
  };

  // Staff fleet state
  const [staffList, setStaffList] = useState<StaffPartner[]>([]);
  const [isAddStaffOpen, setIsAddStaffOpen] = useState(false);
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffPhone, setNewStaffPhone] = useState('');
  const [newStaffSpecialty, setNewStaffSpecialty] = useState('');
  const [newStaffArea, setNewStaffArea] = useState('Bhilai');
  const [newStaffTarget, setNewStaffTarget] = useState(60);

  // Dynamic Service Catalog, Bundles & Categories State
  const [servicesList, setServicesList] = useState<ServiceItem[]>(getServicesList);
  const [bundlesList, setBundlesList] = useState<BundleItem[]>(getBundlesList);
  const [categoriesList, setCategoriesList] = useState<CategoryItem[]>(getCategoriesList);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCategory, setCatalogCategory] = useState<string>('all');
  const [catalogSection, setCatalogSection] = useState<'services' | 'bundles' | 'categories'>('services');
  const [isSyncingCatalog, setIsSyncingCatalog] = useState(false);

  // Quick Inline Price Editing
  const [quickEditServiceId, setQuickEditServiceId] = useState<string | null>(null);
  const [quickPriceInput, setQuickPriceInput] = useState<string>('');
  const [quickOrigPriceInput, setQuickOrigPriceInput] = useState<string>('');

  // Add / Edit Service Modal State
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [serviceToEdit, setServiceToEdit] = useState<ServiceItem | null>(null);
  const [serviceName, setServiceName] = useState('');
  const [serviceCategory, setServiceCategory] = useState<string>('threading');
  const [servicePrice, setServicePrice] = useState<number | ''>('');
  const [serviceOriginalPrice, setServiceOriginalPrice] = useState<number | ''>('');
  const [serviceDisplayPrice, setServiceDisplayPrice] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [serviceDurationMinutes, setServiceDurationMinutes] = useState<number | ''>(30);

  // Add / Edit Bundle Modal State
  const [isBundleModalOpen, setIsBundleModalOpen] = useState(false);
  const [bundleToEdit, setBundleToEdit] = useState<BundleItem | null>(null);
  const [bundleName, setBundleName] = useState('');
  const [bundleServicesRaw, setBundleServicesRaw] = useState('');
  const [bundlePrice, setBundlePrice] = useState<number | ''>('');
  const [bundleOriginalValue, setBundleOriginalValue] = useState<number | ''>('');
  const [bundleIconType, setBundleIconType] = useState<BundleItem['iconType']>('flower');
  const [bundleTagColor, setBundleTagColor] = useState('#e07a8b');

  // Add / Edit Category Modal State
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<CategoryItem | null>(null);
  const [categoryFormLabel, setCategoryFormLabel] = useState('');
  const [categoryFormSlug, setCategoryFormSlug] = useState('');
  const [categoryFormTagline, setCategoryFormTagline] = useState('');
  const [categoryFormImage, setCategoryFormImage] = useState('');

  // Confirmation Modals & Toast State
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'service' | 'bundle' | 'category'; id: string; name: string } | null>(null);
  const [isResetCatalogOpen, setIsResetCatalogOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  // Operational Areas State (Super Admin Only)
  const [operationalAreas, setOperationalAreas] = useState<BhilaiLocationItem[]>(() => getOperationalAreas());
  const [isSyncingAreas, setIsSyncingAreas] = useState(false);
  const [isD1AreasConnected, setIsD1AreasConnected] = useState(false);
  const [areaSearch, setAreaSearch] = useState('');
  const [areaStatusFilter, setAreaStatusFilter] = useState<'all' | 'serviceable' | 'coming_soon'>('all');

  // Add / Edit Operational Area Modal State
  const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
  const [areaToEdit, setAreaToEdit] = useState<BhilaiLocationItem | null>(null);
  const [areaFormName, setAreaFormName] = useState('');
  const [areaFormCity, setAreaFormCity] = useState('Bhilai');
  const [areaFormIsServiceable, setAreaFormIsServiceable] = useState(true);
  const [areaFormLat, setAreaFormLat] = useState('');
  const [areaFormLng, setAreaFormLng] = useState('');
  const [isDetectingAreaGps, setIsDetectingAreaGps] = useState(false);

  // Delete & Reset Areas Confirmation Modal States
  const [areaToDelete, setAreaToDelete] = useState<BhilaiLocationItem | null>(null);
  const [isResetAreasOpen, setIsResetAreasOpen] = useState(false);

  // Customer Reviews & Ratings State (Super Admin Only)
  const [reviewsList, setReviewsList] = useState<CustomerReview[]>(getReviewsList);
  const [isAddReviewModalOpen, setIsAddReviewModalOpen] = useState(false);
  const [newRevName, setNewRevName] = useState('');
  const [newRevRating, setNewRevRating] = useState<number>(5);
  const [newRevComment, setNewRevComment] = useState('');
  const [newRevService, setNewRevService] = useState('');
  const [newRevArea, setNewRevArea] = useState('Smriti Nagar, Bhilai');
  const [newRevBookingRef, setNewRevBookingRef] = useState('');
  const [isSubmittingAdminReview, setIsSubmittingAdminReview] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<CustomerReview | null>(null);

  useEffect(() => {
    if (isOpen) {
      syncReviewsFromRemote().then((res) => {
        if (res.success) setReviewsList(res.reviews);
      });
    }
    const handleRevUpdate = () => {
      setReviewsList(getReviewsList());
    };
    window.addEventListener(REVIEWS_UPDATED_EVENT, handleRevUpdate);
    return () => window.removeEventListener(REVIEWS_UPDATED_EVENT, handleRevUpdate);
  }, [isOpen]);

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => {
      setToastMsg((prev) => (prev === msg ? null : prev));
    }, 3200);
  };

  // Manual Booking Modal State (For Call, Instagram, WhatsApp bookings)
  const [isManualBookingOpen, setIsManualBookingOpen] = useState(false);
  const [manualSource, setManualSource] = useState<BookingSource>('call');
  const [manualClientName, setManualClientName] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [manualArea, setManualArea] = useState('Bhilai Central / Civic Center');
  const [manualAddress, setManualAddress] = useState('');
  const [manualPincode, setManualPincode] = useState('490006');
  const [manualDate, setManualDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [manualSlot, setManualSlot] = useState('10:00 AM - 12:00 PM');
  const [manualSelectedServices, setManualSelectedServices] = useState<
    Array<{ id: string; name: string; price: number; originalPrice?: number; category?: string }>
  >([]);
  const [manualServiceSearch, setManualServiceSearch] = useState('');
  const [customItemName, setCustomItemName] = useState('');
  const [customItemPrice, setCustomItemPrice] = useState<number | ''>('');
  const [manualDiscount, setManualDiscount] = useState<number | ''>('');
  const [manualAssignedStaff, setManualAssignedStaff] = useState('');
  const [manualPaymentStatus, setManualPaymentStatus] = useState<PaymentStatus>('Pending Collection');
  const [manualNotes, setManualNotes] = useState('');
  const [isSubmittingManual, setIsSubmittingManual] = useState(false);
  const [manualSuccessRecord, setManualSuccessRecord] = useState<BookingRecord | null>(null);

  const resetManualBookingForm = () => {
    setManualSource('call');
    setManualClientName('');
    setManualPhone('');
    setManualArea('Bhilai Central / Civic Center');
    setManualAddress('');
    setManualPincode('490006');
    const today = new Date();
    setManualDate(today.toISOString().split('T')[0]);
    setManualSlot('10:00 AM - 12:00 PM');
    setManualSelectedServices([]);
    setManualServiceSearch('');
    setCustomItemName('');
    setCustomItemPrice('');
    setManualDiscount('');
    setManualAssignedStaff('');
    setManualPaymentStatus('Pending Collection');
    setManualNotes('');
    setManualSuccessRecord(null);
  };

  const handleAddServiceToManual = (item: { id: string; name: string; price: number; originalPrice?: number; category?: string }) => {
    setManualSelectedServices((prev) => [
      ...prev,
      {
        id: item.id + '-' + Date.now(),
        name: item.name,
        price: item.price,
        originalPrice: item.originalPrice || Math.round(item.price * 1.35),
        category: item.category || 'General'
      }
    ]);
  };

  const handleRemoveServiceFromManual = (index: number) => {
    setManualSelectedServices((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleAddCustomServiceToManual = () => {
    if (!customItemName.trim()) return;
    const priceNum = Number(customItemPrice) || 0;
    if (priceNum <= 0) {
      alert('Please enter a valid price for the custom service.');
      return;
    }
    setManualSelectedServices((prev) => [
      ...prev,
      {
        id: 'custom-' + Date.now(),
        name: customItemName.trim(),
        price: priceNum,
        originalPrice: Math.round(priceNum * 1.3),
        category: 'Custom / Special Request'
      }
    ]);
    setCustomItemName('');
    setCustomItemPrice('');
  };

  const manualCatalogPrice = manualSelectedServices.reduce((sum, s) => sum + s.price, 0);
  const manualOriginalValue = manualSelectedServices.reduce(
    (sum, s) => sum + (s.originalPrice || Math.round(s.price * 1.35)),
    0
  );
  const discountVal = Number(manualDiscount) || 0;
  const manualFinalTotal = Math.max(0, manualCatalogPrice - discountVal);
  const manualSavings = Math.max(0, manualOriginalValue - manualFinalTotal);

  const handleSaveManualBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualClientName.trim()) {
      alert('Please enter the client name.');
      return;
    }
    const cleanPhone = manualPhone.replace(/\D/g, '').slice(-10);
    if (cleanPhone.length !== 10) {
      alert('Please enter a valid 10-digit customer mobile number.');
      return;
    }
    if (manualSelectedServices.length === 0) {
      alert('Please select at least one service or package for this booking.');
      return;
    }

    setIsSubmittingManual(true);

    const refId = 'BLISHKA-M' + Math.floor(100000 + Math.random() * 900000);
    const startOtp = Math.floor(1000 + Math.random() * 9000).toString();
    const now = new Date().toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });

    const staffObj = staffList.find((s) => s.id === manualAssignedStaff);

    const sourceTag =
      manualSource === 'call'
        ? '[Source: Call]'
        : manualSource === 'instagram'
        ? '[Source: Instagram]'
        : manualSource === 'whatsapp'
        ? '[Source: WhatsApp]'
        : manualSource === 'walkin'
        ? '[Source: Walk-in]'
        : '[Source: Web]';

    const specialReqWithSource = `${sourceTag} ${manualNotes.trim()}`.trim();

    const record: BookingRecord = {
      bookingRef: refId,
      timestamp: now,
      clientName: manualClientName.trim(),
      phone: cleanPhone,
      area: manualArea,
      address: manualAddress.trim() || manualArea,
      pincode: manualPincode.trim(),
      preferredDate: manualDate,
      preferredSlot: manualSlot,
      specialRequests: specialReqWithSource,
      items: manualSelectedServices,
      couponCode: discountVal > 0 ? 'MANUAL_DISCOUNT' : undefined,
      couponDiscount: discountVal > 0 ? discountVal : undefined,
      servicePerson: staffObj ? `${staffObj.name} (${staffObj.specialty})` : 'To be assigned',
      partnerId: staffObj ? staffObj.id : undefined,
      partnerName: staffObj ? staffObj.name : undefined,
      partnerPhone: staffObj ? staffObj.phone : undefined,
      serviceStatus: staffObj ? 'assigned' : 'confirmed',
      status: staffObj ? 'Assigned' : 'Confirmed',
      totalPrice: manualFinalTotal,
      totalOriginalPrice: manualOriginalValue,
      totalSavings: manualSavings,
      startOtp,
      paymentMethod: 'Pay After Service',
      paymentStatus: manualPaymentStatus,
      bookingSource: manualSource
    };

    try {
      const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
      await createAdminManualBooking(activePin, record);
      showToast(`Manual booking ${refId} created successfully!`);
      setManualSuccessRecord(record);
      loadBookings();
    } catch (err) {
      console.error('Failed to create manual booking:', err);
      alert('Error creating manual booking. Please try again.');
    } finally {
      setIsSubmittingManual(false);
    }
  };

  const openWhatsAppConfirmation = (b: BookingRecord) => {
    const cleanPhone = (b.phone || '').replace(/\D/g, '').slice(-10);
    if (!cleanPhone) {
      alert('No valid phone number for this customer.');
      return;
    }
    const serviceNames = b.items && b.items.length > 0
      ? b.items.map((i) => `• ${i.name} (₹${i.price})`).join('\n')
      : 'Doorstep Beauty Service';

    const sourceNote =
      b.bookingSource === 'instagram'
        ? '(Requested via Instagram)'
        : b.bookingSource === 'call'
        ? '(Requested via Phone Call)'
        : '';

    const msg =
      `🌸 *Blishka Doorstep Parlour - Appointment Confirmed* 🌸\n\n` +
      `Dear ${b.clientName},\n` +
      `Your doorstep beauty appointment ${sourceNote} has been confirmed!\n\n` +
      `📌 *Booking Ref:* ${b.bookingRef}\n` +
      `📅 *Date:* ${b.preferredDate}\n` +
      `⏰ *Time Slot:* ${b.preferredSlot}\n` +
      `📍 *Address:* ${b.address}, ${b.area}\n` +
      `🔐 *Doorstep Start OTP:* ${b.startOtp || 'N/A'}\n` +
      `👩‍🎨 *Assigned Beautician:* ${b.servicePerson || 'Professional Specialist'}\n\n` +
      `✨ *Services Booked:*\n${serviceNames}\n\n` +
      `💰 *Total Amount Payable:* ₹${b.totalPrice}\n` +
      `✅ *Payment Mode:* 100% Pay After Service (Cash / UPI to beautician)\n` +
      `🚚 *Doorstep Travel Fee:* FREE (Waived)\n\n` +
      `Our verified female beauty professional will arrive on time with sanitized tools. For any reschedule or query, reply to this message. Thank you for choosing Blishka! ♡`;

    window.open(`https://wa.me/91${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const renderChannelBadge = (b: BookingRecord) => {
    const isCall = b.bookingSource === 'call' || (b.specialRequests && b.specialRequests.includes('[Source: Call]'));
    const isInsta = b.bookingSource === 'instagram' || (b.specialRequests && b.specialRequests.includes('[Source: Instagram]'));
    const isWhatsApp = b.bookingSource === 'whatsapp' || (b.specialRequests && b.specialRequests.includes('[Source: WhatsApp]'));
    const isWalkin = b.bookingSource === 'walkin' || (b.specialRequests && b.specialRequests.includes('[Source: Walk-in]'));

    if (isCall) {
      return (
        <span className="channel-badge channel-badge-call" title="Booked via Direct Phone Call">
          <PhoneCall size={10} /> Call
        </span>
      );
    }
    if (isInsta) {
      return (
        <span className="channel-badge channel-badge-instagram" title="Booked via Instagram DM / Request">
          <InstagramIcon size={10} /> Instagram
        </span>
      );
    }
    if (isWhatsApp) {
      return (
        <span className="channel-badge channel-badge-whatsapp" title="Booked via WhatsApp Chat">
          <WhatsAppIcon size={10} /> WhatsApp
        </span>
      );
    }
    if (isWalkin) {
      return (
        <span className="channel-badge channel-badge-walkin" title="Walk-in / Offline booking">
          Walk-in
        </span>
      );
    }
    return (
      <span className="channel-badge channel-badge-web" title="Direct Website Booking">
        Web
      </span>
    );
  };

  useEffect(() => {
    const unsub = subscribeToCatalogUpdates(() => {
      setServicesList(getServicesList());
      setBundlesList(getBundlesList());
      setCategoriesList(getCategoriesList());
    });
    const handleAreasUpdate = () => {
      setOperationalAreas(getOperationalAreas());
    };
    window.addEventListener('blishka_operational_areas_changed', handleAreasUpdate);
    return () => {
      unsub();
      window.removeEventListener('blishka_operational_areas_changed', handleAreasUpdate);
    };
  }, []);

  const loadBookings = async () => {
    setIsLoading(true);
    try {
      const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
      const [res, servicesRes, staffRes, catRes, bndRes, areasRes] = await Promise.all([
        getAdminBookings(activePin),
        syncServicesFromRemote(activePin),
        fetchStaffPartnersFromRemote(activePin),
        syncCategoriesFromRemote(activePin),
        syncBundlesFromRemote(activePin),
        syncOperationalAreasFromRemote(activePin)
      ]);
      setBookings(res.bookings);
      setIsD1Connected(res.isD1Connected || servicesRes.isD1Connected || staffRes.isD1Connected || catRes.isD1Connected || bndRes.isD1Connected || areasRes.isD1Connected);
      setIsD1AreasConnected(areasRes.isD1Connected);
      setStaffList(staffRes.partners);
      if (servicesRes.services && servicesRes.services.length > 0) {
        setServicesList(servicesRes.services);
      }
      if (catRes.categories && catRes.categories.length > 0) {
        setCategoriesList(catRes.categories);
      }
      if (bndRes.bundles && bndRes.bundles.length > 0) {
        setBundlesList(bndRes.bundles);
      }
      if (areasRes.areas && areasRes.areas.length > 0) {
        setOperationalAreas(areasRes.areas);
      }
    } catch (err) {
      console.warn('Failed to load admin bookings or sync catalog:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadBookings();

    const handleFocus = () => {
      loadBookings();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isOpen, adminPin]);

  if (!isOpen) return null;

  // Catalog Management Handlers
  const handleOpenAddService = () => {
    setServiceToEdit(null);
    setServiceName('');
    const validCats = categoriesList.filter((c) => c.id !== 'all' && c.id !== 'bundles');
    setServiceCategory(validCats.length > 0 ? validCats[0].id : 'threading');
    setServicePrice('');
    setServiceOriginalPrice('');
    setServiceDisplayPrice('');
    setServiceDescription('');
    setServiceDurationMinutes(30);
    setIsServiceModalOpen(true);
  };

  const handleOpenEditService = (s: ServiceItem) => {
    setServiceToEdit(s);
    setServiceName(s.name);
    setServiceCategory(s.category);
    setServicePrice(s.price);
    setServiceOriginalPrice(s.originalPrice);
    setServiceDisplayPrice(s.displayPrice || '');
    setServiceDescription(s.description || '');
    setServiceDurationMinutes(s.durationMinutes || 30);
    setIsServiceModalOpen(true);
  };

  const handleSaveService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceName.trim() || servicePrice === '') return;
    const priceNum = Number(servicePrice);
    const origPriceNum = serviceOriginalPrice !== '' ? Number(serviceOriginalPrice) : Math.round(priceNum * 1.35);
    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    const durationNum = serviceDurationMinutes !== '' ? Math.max(5, Number(serviceDurationMinutes) || 30) : 30;

    const item: ServiceItem = {
      id: serviceToEdit ? serviceToEdit.id : `srv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: serviceName.trim(),
      category: serviceCategory,
      price: priceNum,
      originalPrice: origPriceNum,
      displayPrice: serviceDisplayPrice.trim() || undefined,
      description: serviceDescription.trim() || undefined,
      durationMinutes: durationNum
    };

    setIsServiceModalOpen(false);
    await saveService(item, activePin);
    setServicesList(getServicesList());
    showToast(serviceToEdit ? `Updated "${item.name}" (Synced to Cloudflare D1)` : `Added new service "${item.name}" (Synced to Cloudflare D1)`);
  };

  const handleStartQuickPrice = (s: ServiceItem) => {
    setQuickEditServiceId(s.id);
    setQuickPriceInput(s.price.toString());
    setQuickOrigPriceInput(s.originalPrice.toString());
  };

  const handleSaveQuickPrice = async (id: string) => {
    const newPrice = Number(quickPriceInput);
    const newOrig = quickOrigPriceInput !== '' ? Number(quickOrigPriceInput) : undefined;
    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    if (!isNaN(newPrice) && newPrice >= 0) {
      setQuickEditServiceId(null);
      await updateServicePrice(id, newPrice, newOrig, activePin);
      setServicesList(getServicesList());
      showToast(`Price updated to ₹${newPrice} (Synced to Cloudflare D1)`);
    } else {
      setQuickEditServiceId(null);
    }
  };

  const handleOpenAddBundle = () => {
    setBundleToEdit(null);
    setBundleName('');
    setBundleServicesRaw('');
    setBundlePrice('');
    setBundleOriginalValue('');
    setBundleIconType('flower');
    setBundleTagColor('#e07a8b');
    setIsBundleModalOpen(true);
  };

  const handleOpenEditBundle = (b: BundleItem) => {
    setBundleToEdit(b);
    setBundleName(b.name);
    setBundleServicesRaw(b.servicesIncluded.join(', '));
    setBundlePrice(b.price);
    setBundleOriginalValue(b.originalValue);
    setBundleIconType(b.iconType);
    setBundleTagColor(b.tagColor || '#e07a8b');
    setIsBundleModalOpen(true);
  };

  const handleSaveBundle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bundleName.trim() || bundlePrice === '') return;
    const priceNum = Number(bundlePrice);
    const origValNum = bundleOriginalValue !== '' ? Number(bundleOriginalValue) : Math.round(priceNum * 1.3);
    const services = bundleServicesRaw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const bItem: BundleItem = {
      id: bundleToEdit ? bundleToEdit.id : `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: bundleName.trim(),
      servicesIncluded: services.length > 0 ? services : ['Doorstep Service Combo'],
      price: priceNum,
      originalValue: origValNum,
      saveAmount: Math.max(0, origValNum - priceNum),
      iconType: bundleIconType,
      tagColor: bundleTagColor
    };

    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    await saveBundleRemote(bItem, activePin);
    setBundlesList(getBundlesList());
    setIsBundleModalOpen(false);
    showToast(bundleToEdit ? `Updated bundle "${bItem.name}" (Synced across all devices & D1)` : `Created bundle "${bItem.name}" (Synced across all devices & D1)`);
  };

  // Category Management Handlers
  const handleOpenAddCategory = () => {
    setCategoryToEdit(null);
    setCategoryFormLabel('');
    setCategoryFormSlug('');
    setCategoryFormTagline('');
    setCategoryFormImage('');
    setIsCategoryModalOpen(true);
  };

  const handleOpenEditCategory = (cat: CategoryItem) => {
    setCategoryToEdit(cat);
    setCategoryFormLabel(cat.label);
    setCategoryFormSlug(cat.id);
    setCategoryFormTagline(cat.tagline);
    setCategoryFormImage(cat.image || '');
    setIsCategoryModalOpen(true);
  };

  const handleSaveCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryFormLabel.trim()) return;
    const slug = (
      categoryFormSlug.trim() ||
      categoryFormLabel.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')
    ).replace(/^-|-$/g, '');

    const catItem: CategoryItem = {
      id: categoryToEdit ? categoryToEdit.id : slug,
      label: categoryFormLabel.trim(),
      tagline: categoryFormTagline.trim() || 'Doorstep beauty parlour services',
      image: categoryFormImage.trim() || undefined
    };

    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    await saveCategoryRemote(catItem, activePin);
    setCategoriesList(getCategoriesList());
    setIsCategoryModalOpen(false);
    showToast(categoryToEdit ? `Updated category "${catItem.label}" (Synced across all devices & D1)` : `Created category "${catItem.label}" (Synced across all devices & D1)`);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    if (deleteTarget.type === 'service') {
      await deleteService(deleteTarget.id, activePin);
      setServicesList(getServicesList());
      showToast(`Deleted service "${deleteTarget.name}" (Synced across all systems)`);
    } else if (deleteTarget.type === 'bundle') {
      await deleteBundleRemote(deleteTarget.id, activePin);
      setBundlesList(getBundlesList());
      showToast(`Deleted bundle "${deleteTarget.name}" (Synced across all devices & D1)`);
    } else if (deleteTarget.type === 'category') {
      const res = await deleteCategoryRemote(deleteTarget.id, activePin);
      if (!res.success) {
        showToast(res.error || 'Cannot delete category');
      } else {
        setCategoriesList(getCategoriesList());
        setServicesList(getServicesList());
        if (catalogCategory === deleteTarget.id) {
          setCatalogCategory('all');
        }
        showToast(`Deleted category "${deleteTarget.name}" (Synced across all systems & D1)`);
      }
    }
    setDeleteTarget(null);
  };

  const handleResetCatalog = async () => {
    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    await resetCatalogToDefaults(activePin);
    setServicesList(getServicesList());
    setBundlesList(getBundlesList());
    setCategoriesList(getCategoriesList());
    setCatalogCategory('all');
    setIsResetCatalogOpen(false);
    showToast('Catalog restored to original factory defaults (Synced to D1)');
  };

  const handleManualCatalogSync = async () => {
    setIsSyncingCatalog(true);
    try {
      const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
      const res = await syncAllCatalogFromRemote(activePin);
      setServicesList(getServicesList());
      setCategoriesList(getCategoriesList());
      setBundlesList(getBundlesList());
      if (res.isD1Connected) {
        showToast('Services, Categories & Bundles synced with Cloudflare D1!');
      } else {
        showToast('Catalog refreshed across all devices');
      }
    } catch {
      showToast('Could not reach remote database, using local cache');
    } finally {
      setIsSyncingCatalog(false);
    }
  };

  const handleStatusChange = async (refId: string, newStatus: string) => {
    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    setBookings((prev) =>
      prev.map((b) => (b.bookingRef === refId ? { ...b, status: newStatus, serviceStatus: newStatus.toLowerCase() as ServiceStatus } : b))
    );
    await updateAdminBooking(activePin, refId, {
      status: newStatus,
      serviceStatus: newStatus.toLowerCase() as ServiceStatus
    });
  };

  const handleAssignStaff = async (refId: string, staffId: string) => {
    const staff = staffList.find((s) => s.id === staffId);
    if (!staff) return;

    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    await assignStaffToBooking(activePin, refId, staff);

    setBookings((prev) =>
      prev.map((b) =>
        b.bookingRef === refId
          ? {
              ...b,
              partnerId: staff.id,
              partnerName: staff.name,
              partnerPhone: staff.phone,
              servicePerson: `${staff.name} (${staff.specialty})`,
              serviceStatus: 'assigned',
              status: 'Assigned'
            }
          : b
      )
    );
  };

  const handleDelete = async (refId: string) => {
    if (confirm(`Are you sure you want to delete booking ${refId} permanently?`)) {
      setBookings((prev) => prev.filter((b) => b.bookingRef !== refId));
      const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
      await deleteAdminBooking(activePin, refId);
    }
  };

  const handleAdminRescheduleSuccess = (updated: BookingRecord) => {
    setBookings((prev) =>
      prev.map((b) => (b.bookingRef === updated.bookingRef ? updated : b))
    );
    showToast(`Rescheduled ${updated.bookingRef} to ${updated.preferredDate} (${updated.preferredSlot})`);
  };

  const handleExitAdmin = () => {
    if (onLockOut) {
      onLockOut();
    } else {
      sessionStorage.removeItem('blishka_admin_auth');
      sessionStorage.removeItem('blishka_admin_pin');
      onClose();
    }
  };

  // Toggle staff duty in admin
  const handleToggleStaffDuty = async (sId: string) => {
    const target = staffList.find((s) => s.id === sId);
    if (!target) return;
    const nextStatus = !target.isOnline;
    const updated = staffList.map((s) => (s.id === sId ? { ...s, isOnline: nextStatus } : s));
    setStaffList(updated);
    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    await toggleStaffDutyRemote(sId, nextStatus, activePin);
  };

  // Delete beautician permanently
  const handleDeleteStaff = async (staffId: string, staffName: string) => {
    if (!window.confirm(`Are you sure you want to remove beautician "${staffName}"? This will permanently delete their profile from Cloudflare D1.`)) {
      return;
    }
    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    setStaffList((prev) => prev.filter((s) => s.id !== staffId));
    await deleteStaffPartnerRemote(staffId, activePin);
  };

  // Add new staff partner
  const handleAddStaffSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStaffName.trim() || !newStaffPhone.trim()) return;

    const newPartner: StaffPartner = {
      id: `staff-${Date.now().toString().slice(-4)}`,
      name: newStaffName.trim(),
      phone: newStaffPhone.trim(),
      pin: '1234',
      specialty: newStaffSpecialty.trim() || 'Beauty Professional',
      area: newStaffArea.trim() || 'Bhilai / Durg',
      rating: 5.0,
      totalJobs: 0,
      monthlyTarget: Number(newStaffTarget) || 60,
      isOnline: true,
      isActive: true
    };

    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    setStaffList((prev) => [newPartner, ...prev.filter((p) => p.id !== newPartner.id)]);
    setIsAddStaffOpen(false);
    setNewStaffName('');
    setNewStaffPhone('');
    setNewStaffSpecialty('');

    await saveStaffPartnerRemote(newPartner, activePin);
    const refetched = await fetchStaffPartnersFromRemote(activePin);
    if (refetched && Array.isArray(refetched.partners)) {
      setStaffList(refetched.partners);
    }
  };

  // Operational Areas Action Handlers (Super Admin Exclusive)
  const handleOpenAddArea = () => {
    setAreaToEdit(null);
    setAreaFormName('');
    setAreaFormCity('Bhilai');
    setAreaFormIsServiceable(true);
    setAreaFormLat('');
    setAreaFormLng('');
    setIsAreaModalOpen(true);
  };

  const handleOpenEditArea = (area: BhilaiLocationItem) => {
    setAreaToEdit(area);
    setAreaFormName(area.name);
    setAreaFormCity(area.city || 'Bhilai');
    setAreaFormIsServiceable(area.isServiceable);
    setAreaFormLat(area.lat !== undefined ? String(area.lat) : '');
    setAreaFormLng(area.lng !== undefined ? String(area.lng) : '');
    setIsAreaModalOpen(true);
  };

  const handleSaveArea = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanName = areaFormName.trim();
    if (!cleanName) {
      alert('Please enter an area/colony name.');
      return;
    }

    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    const latNum = areaFormLat.trim() !== '' && !isNaN(Number(areaFormLat)) ? Number(areaFormLat) : undefined;
    const lngNum = areaFormLng.trim() !== '' && !isNaN(Number(areaFormLng)) ? Number(areaFormLng) : undefined;

    const areaItem: BhilaiLocationItem = {
      id: areaToEdit ? areaToEdit.id : `area-${cleanName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || Date.now()}`,
      name: cleanName,
      city: areaFormCity.trim() || 'Bhilai',
      isServiceable: areaFormIsServiceable,
      lat: latNum,
      lng: lngNum
    };

    await saveOperationalAreaRemote(areaItem, activePin);
    setOperationalAreas(getOperationalAreas());
    setIsAreaModalOpen(false);
    showToast(areaToEdit ? `Updated area: ${cleanName}` : `Added operational area: ${cleanName}`);
  };

  const handleToggleAreaServiceable = async (area: BhilaiLocationItem) => {
    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    const newStatus = !area.isServiceable;
    await toggleAreaServiceableRemote(area.id, newStatus, activePin);
    setOperationalAreas(getOperationalAreas());
    showToast(`${area.name}: ${newStatus ? 'Activated for Delivery' : 'Marked Coming Soon'}`);
  };

  const handleDeleteAreaConfirm = async () => {
    if (!areaToDelete) return;
    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    await deleteOperationalAreaRemote(areaToDelete.id, activePin);
    setOperationalAreas(getOperationalAreas());
    showToast(`Removed operational area: ${areaToDelete.name}`);
    setAreaToDelete(null);
  };

  const handleResetAreasConfirm = async () => {
    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    await resetOperationalAreasRemote(activePin);
    setOperationalAreas(getOperationalAreas());
    showToast('Operational areas reset to factory defaults');
    setIsResetAreasOpen(false);
  };

  const handleSyncAreasManual = async () => {
    setIsSyncingAreas(true);
    const activePin = adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994';
    const res = await syncOperationalAreasFromRemote(activePin);
    setIsSyncingAreas(false);
    setIsD1AreasConnected(res.isD1Connected);
    setOperationalAreas(res.areas);
    showToast(res.isD1Connected ? 'Synced operational areas with Cloudflare D1' : 'Operational areas refreshed from local cache');
  };

  const handleDetectFormGps = async () => {
    setIsDetectingAreaGps(true);
    const res = await detectLiveGeolocation({ forceFresh: true });
    setIsDetectingAreaGps(false);
    if (res.success && res.lat !== undefined && res.lng !== undefined) {
      setAreaFormLat(res.lat.toFixed(6));
      setAreaFormLng(res.lng.toFixed(6));
      showToast('Detected current device GPS coordinates');
    } else {
      alert(res.error || 'Failed to detect device GPS coordinates');
    }
  };

  // Filter bookings
  const filteredBookings = bookings.filter((b) => {
    const matchesSearch =
      !searchQuery ||
      b.bookingRef.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.phone.includes(searchQuery) ||
      b.area.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.startOtp && b.startOtp.includes(searchQuery)) ||
      (b.bookingSource && b.bookingSource.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (b.specialRequests && b.specialRequests.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'unassigned' && (!b.partnerId || b.servicePerson === 'To be assigned')) ||
      (statusFilter === 'assigned' && b.serviceStatus === 'assigned') ||
      (statusFilter === 'in_progress' && (b.serviceStatus === 'in_progress' || b.serviceStatus === 'in_transit' || b.serviceStatus === 'arrived')) ||
      (statusFilter === 'completed' && (b.serviceStatus === 'completed' || b.status === 'Completed')) ||
      (statusFilter === 'cancelled' && (b.serviceStatus === 'cancelled' || b.status === 'Cancelled'));

    const matchesChannel =
      channelFilter === 'all' ||
      (channelFilter === 'call' && (b.bookingSource === 'call' || (b.specialRequests && b.specialRequests.includes('[Source: Call]')))) ||
      (channelFilter === 'instagram' && (b.bookingSource === 'instagram' || (b.specialRequests && b.specialRequests.includes('[Source: Instagram]')))) ||
      (channelFilter === 'whatsapp' && (b.bookingSource === 'whatsapp' || (b.specialRequests && b.specialRequests.includes('[Source: WhatsApp]')))) ||
      (channelFilter === 'web' && (!b.bookingSource || b.bookingSource === 'web' || (b.specialRequests && b.specialRequests.includes('[Source: Web]'))));

    return matchesSearch && matchesStatus && matchesChannel;
  });

  // Financial calculations
  const totalRevenue = bookings.reduce((sum, b) => sum + b.totalPrice, 0);
  const totalSavings = bookings.reduce((sum, b) => sum + b.totalSavings, 0);

  const cashCollected = bookings
    .filter((b) => b.paymentStatus === 'Collected via Cash')
    .reduce((sum, b) => sum + b.totalPrice, 0);

  const upiCollected = bookings
    .filter((b) => b.paymentStatus === 'Collected via UPI')
    .reduce((sum, b) => sum + b.totalPrice, 0);

  const pendingCollection = bookings
    .filter((b) => b.serviceStatus !== 'completed' && b.serviceStatus !== 'cancelled')
    .reduce((sum, b) => sum + b.totalPrice, 0);

  const activeDispatchesCount = bookings.filter(
    (b) => b.serviceStatus === 'assigned' || b.serviceStatus === 'in_transit' || b.serviceStatus === 'arrived' || b.serviceStatus === 'in_progress'
  ).length;

  const completedVisitsCount = bookings.filter(
    (b) => b.serviceStatus === 'completed' || b.status === 'Completed'
  ).length;

  return (
    <div className="portal-fullscreen-shell">
      {/* ── 1. ENTERPRISE DASHBOARD TOP HEADER ── */}
      {/* ── 1. CLEAN DASHBOARD TOP HEADER ── */}
      <header className="portal-header">
        {/* Left: Branding & Status */}
        <div className="portal-header-left">
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: currentRole === 'super_admin'
                ? 'linear-gradient(135deg, #b8860b 0%, #7d5a00 100%)'
                : 'linear-gradient(135deg, var(--blishka-berry) 0%, #4a0a1f 100%)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: currentRole === 'super_admin'
                ? '0 2px 8px rgba(184, 134, 11, 0.25)'
                : '0 2px 8px rgba(125, 26, 56, 0.2)',
              flexShrink: 0
            }}
          >
            {currentRole === 'super_admin' ? <Crown size={18} /> : <ShieldCheck size={18} />}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
            <h2
              style={{
                margin: 0,
                fontSize: '1.05rem',
                fontWeight: 800,
                color: currentRole === 'super_admin' ? '#7d5a00' : 'var(--blishka-berry)',
                fontFamily: 'var(--font-serif)',
                letterSpacing: '-0.3px'
              }}
            >
              {currentRole === 'super_admin' ? 'Blishka Super Admin' : 'Blishka Admin'}
            </h2>

            {currentRole === 'super_admin' ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  background: 'rgba(184, 134, 11, 0.12)',
                  color: '#9a7000',
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  padding: '0.12rem 0.5rem',
                  borderRadius: '999px',
                  border: '1px solid rgba(184, 134, 11, 0.3)'
                }}
              >
                <Crown size={10} />
                <span>EXECUTIVE</span>
              </span>
            ) : null}

            {isD1Connected ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  background: 'rgba(46, 125, 50, 0.1)',
                  color: '#2e7d32',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  padding: '0.12rem 0.45rem',
                  borderRadius: '999px'
                }}
              >
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#2e7d32' }} />
                <span>D1 Live</span>
              </span>
            ) : (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  background: 'rgba(125, 26, 56, 0.08)',
                  color: 'var(--blishka-berry)',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  padding: '0.12rem 0.45rem',
                  borderRadius: '999px'
                }}
              >
                <span>Local Sync</span>
              </span>
            )}
          </div>
        </div>

        {/* Right: Clean, Non-Repeated Controls */}
        <div className="portal-header-right">
          <button
            type="button"
            onClick={loadBookings}
            className="btn btn-outline btn-sm"
            style={{ gap: '0.35rem', padding: '0.32rem 0.65rem', fontSize: '0.78rem' }}
            title="Refresh live bookings and status"
          >
            <RefreshCw size={13} className={isLoading ? 'spin-icon' : ''} />
            <span>Sync</span>
          </button>

          {currentRole === 'super_admin' && (
            <button
              type="button"
              onClick={() => exportMasterBookingsToExcel(bookings)}
              className="btn btn-sm"
              style={{
                gap: '0.35rem',
                background: '#2e7d32',
                color: '#ffffff',
                border: 'none',
                fontWeight: 600,
                padding: '0.32rem 0.65rem',
                fontSize: '0.78rem'
              }}
              title="Download master bookings Excel report"
            >
              <FileSpreadsheet size={13} />
              <span>Export .xlsx</span>
            </button>
          )}

          {/* Install Admin Dashboard as App */}
          {!isStandalone && (
            <button
              type="button"
              onClick={handleOpenInstallModal}
              className="btn btn-sm"
              style={{
                gap: '0.35rem',
                padding: '0.32rem 0.65rem',
                fontSize: '0.78rem',
                background: currentRole === 'super_admin' ? 'rgba(184, 134, 11, 0.12)' : 'rgba(125, 26, 56, 0.08)',
                color: currentRole === 'super_admin' ? '#9a7000' : 'var(--blishka-berry)',
                border: currentRole === 'super_admin' ? '1px solid rgba(184, 134, 11, 0.35)' : '1px solid rgba(125, 26, 56, 0.25)',
                fontWeight: 700
              }}
              title="Install Blishka Admin as a standalone desktop or mobile application"
            >
              <Download size={13} />
              <span>Install<span className="portal-btn-label-desktop"> App</span></span>
            </button>
          )}

          {isStandalone && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                background: 'rgba(46, 125, 50, 0.08)',
                color: '#2e7d32',
                fontSize: '0.68rem',
                fontWeight: 700,
                padding: '0.12rem 0.45rem',
                borderRadius: '999px',
                border: '1px solid rgba(46, 125, 50, 0.2)'
              }}
              title="Running in Standalone Application Mode"
            >
              <ShieldCheck size={10} />
              <span className="portal-btn-label-desktop">App Mode</span>
            </span>
          )}

          {/* Single, Clean Close / Lock Button on the Top Right */}
          <button
            type="button"
            onClick={handleExitAdmin}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'rgba(125, 26, 56, 0.06)',
              border: '1px solid rgba(125, 26, 56, 0.18)',
              color: 'var(--blishka-berry)',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
            title="Lock and Close Admin Console"
            aria-label="Close Admin"
          >
            <X size={16} />
          </button>
        </div>
      </header>

      {/* Mobile Micro KPI Toggle Bar */}
      <div className="portal-mobile-kpi-control">
        <button
          type="button"
          onClick={() => setShowKpiMobile((prev) => !prev)}
          className="portal-mobile-kpi-toggle"
          title="Toggle booking summary counts"
        >
          <span className="portal-mobile-kpi-icon">📊</span>
          <span className="portal-mobile-kpi-summary">
            {currentRole === 'super_admin'
              ? `GMV: ₹${totalRevenue.toLocaleString('en-IN')} · ${bookings.length} Orders`
              : `${bookings.length} Orders · ${activeDispatchesCount} Live`}
          </span>
          <span className="portal-mobile-kpi-chevron">
            {showKpiMobile && !isScrolledDown ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </span>
        </button>
        {isScrolledDown && (
          <span className="portal-mobile-scrolled-hint">
            Counts hidden on scroll ↓
          </span>
        )}
      </div>

      {/* ── 2. KPI METRICS DASHBOARD STRIP ── */}
      {currentRole === 'super_admin' ? (
        /* SUPER ADMIN EXECUTIVE FINANCIAL KPI STRIP */
        <section className={`portal-kpi-strip ${(!showKpiMobile || isScrolledDown) ? 'kpi-hidden-mobile' : ''}`}>
          {/* KPI 1: Gross Value */}
          <div style={{ background: '#fbf9fa', padding: '0.75rem 1rem', borderRadius: '12px', border: '1.5px solid rgba(184, 134, 11, 0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', color: '#b8860b', textTransform: 'uppercase', fontWeight: 800 }}>
                Total GMV (Sales)
              </span>
              <TrendingUp size={14} style={{ color: '#b8860b' }} />
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#7d5a00', marginTop: '0.2rem' }}>
              ₹{totalRevenue.toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
              {bookings.length} Total Bookings
            </div>
          </div>

          {/* KPI 2: Live Dispatches */}
          <div style={{ background: '#fbf9fa', padding: '0.75rem 1rem', borderRadius: '12px', border: '1px solid var(--border-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                Live Dispatches
              </span>
              <Clock size={14} style={{ color: '#e65100' }} />
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#e65100', marginTop: '0.2rem' }}>
              {activeDispatchesCount}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
              In Transit / In Progress
            </div>
          </div>

          {/* KPI 3: Completed Visits */}
          <div style={{ background: '#fbf9fa', padding: '0.75rem 1rem', borderRadius: '12px', border: '1px solid var(--border-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                Completed Visits
              </span>
              <CheckCircle2 size={14} style={{ color: '#2e7d32' }} />
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#2e7d32', marginTop: '0.2rem' }}>
              {completedVisitsCount}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
              {bookings.length > 0 ? Math.round((completedVisitsCount / bookings.length) * 100) : 0}% Completion Rate
            </div>
          </div>

          {/* KPI 4: Cash Collected (Home) */}
          <div style={{ background: '#fbf9fa', padding: '0.75rem 1rem', borderRadius: '12px', border: '1px solid rgba(46,125,50,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', color: '#2e7d32', textTransform: 'uppercase', fontWeight: 700 }}>
                Cash In Hand
              </span>
              <span style={{ fontSize: '0.8rem' }}>💵</span>
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#2e7d32', marginTop: '0.2rem' }}>
              ₹{cashCollected.toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
              Doorstep Cash Collected
            </div>
          </div>

          {/* KPI 5: UPI Received */}
          <div style={{ background: '#fbf9fa', padding: '0.75rem 1rem', borderRadius: '12px', border: '1px solid rgba(125,26,56,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--blishka-berry)', textTransform: 'uppercase', fontWeight: 700 }}>
                UPI Direct QR
              </span>
              <CreditCard size={14} style={{ color: 'var(--blishka-berry)' }} />
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--blishka-berry)', marginTop: '0.2rem' }}>
              ₹{upiCollected.toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
              Bank QR Deposited
            </div>
          </div>

          {/* KPI 6: Pending Collection */}
          <div style={{ background: '#fbf9fa', padding: '0.75rem 1rem', borderRadius: '12px', border: '1px solid var(--border-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                Pending Collections
              </span>
              <span style={{ fontSize: '0.8rem' }}>⏳</span>
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--blishka-gold)', marginTop: '0.2rem' }}>
              ₹{pendingCollection.toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
              Unsettled Orders
            </div>
          </div>
        </section>
      ) : (
        /* OPERATIONS ADMIN KPI STRIP (STRICTLY OPERATIONAL - MONEY IS COMPLETELY HIDDEN) */
        <section className={`portal-kpi-strip ${(!showKpiMobile || isScrolledDown) ? 'kpi-hidden-mobile' : ''}`}>
          {/* Op KPI 1: Total Appointments */}
          <div style={{ background: '#fbf9fa', padding: '0.75rem 1rem', borderRadius: '12px', border: '1px solid var(--border-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                Total Bookings
              </span>
              <Calendar size={14} style={{ color: 'var(--blishka-berry)' }} />
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--blishka-berry)', marginTop: '0.2rem' }}>
              {bookings.length}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
              Customer Appointments
            </div>
          </div>

          {/* Op KPI 2: Live Dispatches */}
          <div style={{ background: '#fbf9fa', padding: '0.75rem 1rem', borderRadius: '12px', border: '1px solid var(--border-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                Live In Transit
              </span>
              <Clock size={14} style={{ color: '#e65100' }} />
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#e65100', marginTop: '0.2rem' }}>
              {activeDispatchesCount}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
              Active Doorstep Visits
            </div>
          </div>

          {/* Op KPI 3: Completed Visits */}
          <div style={{ background: '#fbf9fa', padding: '0.75rem 1rem', borderRadius: '12px', border: '1px solid var(--border-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                Completed Jobs
              </span>
              <CheckCircle2 size={14} style={{ color: '#2e7d32' }} />
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#2e7d32', marginTop: '0.2rem' }}>
              {completedVisitsCount}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
              {bookings.length > 0 ? Math.round((completedVisitsCount / bookings.length) * 100) : 0}% Execution Rate
            </div>
          </div>

          {/* Op KPI 4: Beauticians On Duty */}
          <div style={{ background: '#fbf9fa', padding: '0.75rem 1rem', borderRadius: '12px', border: '1px solid var(--border-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                Active Beauticians
              </span>
              <Users size={14} style={{ color: 'var(--blishka-berry)' }} />
            </div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--blishka-berry)', marginTop: '0.2rem' }}>
              {staffList.filter((s) => s.isOnline).length} / {staffList.length}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
              Staff Available On Duty
            </div>
          </div>
        </section>
      )}

      {/* ── 3. DASHBOARD NAVIGATION TABS (Horizontal Scroll on Mobile) ── */}
      <nav className="portal-tabs-bar">
        {[
          { id: 'dispatch', label: `Live Orders (${bookings.length})`, icon: Clock },
          { id: 'fleet', label: `Beauticians (${staffList.length})`, icon: Users },
          ...(currentRole === 'super_admin'
            ? [
                { id: 'finance', label: `Collections Reconciliation (GMV)`, icon: CreditCard },
                { id: 'areas', label: `Operational Areas (${operationalAreas.length})`, icon: MapPin },
                { id: 'reviews', label: `Reviews & Ratings (${reviewsList.length})`, icon: Star }
              ]
            : []),
          { id: 'catalog', label: `Service Catalog (${servicesList.length})`, icon: Sparkles }
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = adminTab === tab.id;
          const isFinance = tab.id === 'finance';
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setAdminTab(tab.id as any)}
              className="portal-tab-btn"
              style={{
                borderBottom: isActive
                  ? isFinance
                    ? '3px solid #b8860b'
                    : '3px solid var(--blishka-berry)'
                  : '3px solid transparent',
                color: isActive
                  ? isFinance
                    ? '#b8860b'
                    : 'var(--blishka-berry)'
                  : 'var(--color-text-muted)',
                fontWeight: isActive ? 700 : 500
              }}
            >
              <Icon size={15} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>

      {/* ── 4. DASHBOARD CONTENT VIEWPORT ── */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* ── TAB 1: LIVE DISPATCH & ORDERS BOARD ── */}
        {adminTab === 'dispatch' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Toolbar */}
            <div className={`portal-dispatch-toolbar ${isScrolledDown ? 'toolbar-compact-mobile' : ''}`}>
              {/* Row 1: Search, Mobile Filters Toggle, and + Manual Booking */}
              <div className="portal-toolbar-top-row">
                <div className="portal-search-wrapper">
                  <Search size={15} className="portal-search-icon" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search client, phone, OTP, ref..."
                    className="portal-search-input"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="portal-search-clear"
                      title="Clear search"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>

                {/* Universal Filter Toggle Button (Desktop & Mobile) */}
                <button
                  type="button"
                  onClick={() => setShowFilters((prev) => !prev)}
                  className={`portal-filter-toggle-btn ${showFilters ? 'filters-expanded' : ''} ${(channelFilter !== 'all' || statusFilter !== 'all') ? 'active-filter' : ''}`}
                  title="Toggle Channel & Status Filters"
                >
                  <Filter size={13} />
                  <span className="filter-text">{showFilters ? 'Hide Filters' : 'Filters'}</span>
                  {(channelFilter !== 'all' || statusFilter !== 'all') && (
                    <span className="filter-badge">
                      {(channelFilter !== 'all' ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0)}
                    </span>
                  )}
                </button>

                {/* + Manual Booking Primary Action Button */}
                <button
                  type="button"
                  onClick={() => {
                    resetManualBookingForm();
                    setIsManualBookingOpen(true);
                  }}
                  className="portal-manual-booking-btn"
                  title="Create manual booking from Phone Call or Instagram request"
                >
                  <PhoneCall size={13} />
                  <span className="btn-label-desktop">+ Manual Booking</span>
                  <span className="btn-label-mobile">+ Book</span>
                </button>
              </div>

              {/* Filter Section: Channels & Status Tracks (Hidden by default, opens only when clicked) */}
              <div className={`portal-toolbar-filter-section ${(!showFilters || isScrolledDown) ? 'filters-hidden' : ''}`}>
                {/* Channel Source Filter Track */}
                <div className="portal-filter-track-container">
                  <span className="portal-filter-track-label">Source:</span>
                  <div className="portal-filter-chips-track">
                    {[
                      { id: 'all', label: 'All Sources' },
                      { id: 'call', label: '📞 Call' },
                      { id: 'instagram', label: '📸 Insta' },
                      { id: 'whatsapp', label: '💬 WA' },
                      { id: 'web', label: '🌐 Web' }
                    ].map((c) => {
                      const isSelected = channelFilter === c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setChannelFilter(c.id as any)}
                          className={`portal-filter-chip ${isSelected ? 'active-source' : ''}`}
                        >
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Status Filter Track */}
                <div className="portal-filter-track-container">
                  <span className="portal-filter-track-label">Status:</span>
                  <div className="portal-filter-chips-track">
                    {[
                      { id: 'all', label: 'All Status' },
                      { id: 'unassigned', label: 'Unassigned' },
                      { id: 'assigned', label: 'Assigned' },
                      { id: 'in_progress', label: 'In Transit' },
                      { id: 'completed', label: 'Completed' },
                      { id: 'cancelled', label: 'Cancelled' }
                    ].map((f) => {
                      const isSelected = statusFilter === f.id;
                      return (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => setStatusFilter(f.id)}
                          className={`portal-filter-chip ${isSelected ? 'active-status' : ''}`}
                        >
                          {f.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Active Filter Clear */}
                {(channelFilter !== 'all' || statusFilter !== 'all') && (
                  <button
                    type="button"
                    onClick={() => {
                      setChannelFilter('all');
                      setStatusFilter('all');
                    }}
                    className="portal-filter-reset-btn"
                  >
                    Reset Filters
                  </button>
                )}
              </div>
            </div>

            {/* Bookings Full Table (Desktop) & Cards (Mobile) */}
            <div
              style={{ flex: 1, overflowY: 'auto', padding: '1rem', background: '#f8fafc' }}
              className="portal-body-container"
              onScroll={handleBodyScroll}
            >
              {filteredBookings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3.5rem 1rem', background: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-soft)' }}>
                  <Layers size={40} style={{ color: 'var(--color-text-muted)', margin: '0 auto 0.75rem' }} />
                  <h4 style={{ margin: 0, color: 'var(--color-text-main)' }}>No bookings matching your criteria</h4>
                  <p style={{ fontSize: '0.84rem', color: 'var(--color-text-muted)', marginTop: '0.35rem' }}>
                    Try adjusting your search query or status filter.
                  </p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="admin-desktop-table-container">
                    <div
                      style={{
                        background: '#ffffff',
                        borderRadius: '16px',
                        border: '1px solid var(--border-soft)',
                        boxShadow: 'var(--shadow-sm)',
                        overflow: 'hidden'
                      }}
                    >
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                        <thead>
                          <tr style={{ background: '#fbf9fa', borderBottom: '1.5px solid var(--border-soft)', textAlign: 'left' }}>
                            <th style={{ padding: '0.85rem 1rem', fontWeight: 700, color: 'var(--color-text-main)' }}>Ref & Start OTP</th>
                            <th style={{ padding: '0.85rem 1rem', fontWeight: 700, color: 'var(--color-text-main)' }}>Client Details</th>
                            <th style={{ padding: '0.85rem 1rem', fontWeight: 700, color: 'var(--color-text-main)' }}>Schedule & Slot</th>
                            <th style={{ padding: '0.85rem 1rem', fontWeight: 700, color: 'var(--color-text-main)' }}>Services Booked</th>
                            <th style={{ padding: '0.85rem 1rem', fontWeight: 700, color: 'var(--color-text-main)' }}>Amount & Payment</th>
                            <th style={{ padding: '0.85rem 1rem', fontWeight: 700, color: 'var(--color-text-main)' }}>Assigned Beautician</th>
                            <th style={{ padding: '0.85rem 1rem', fontWeight: 700, color: 'var(--color-text-main)' }}>Service Status</th>
                            <th style={{ padding: '0.85rem 1rem', fontWeight: 700, color: 'var(--color-text-main)', textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredBookings.map((b) => (
                            <tr key={b.bookingRef} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                              {/* Ref & Start OTP */}
                              <td style={{ padding: '0.85rem 1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
                                  <strong style={{ color: 'var(--blishka-berry)', fontSize: '0.9rem' }}>{b.bookingRef}</strong>
                                  {renderChannelBadge(b)}
                                </div>
                                <div style={{ marginTop: '0.2rem' }}>
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      fontSize: '0.74rem',
                                      color: '#2e7d32',
                                      fontWeight: 800,
                                      fontFamily: 'monospace',
                                      background: 'rgba(46, 125, 50, 0.1)',
                                      padding: '0.15rem 0.45rem',
                                      borderRadius: '6px'
                                    }}
                                  >
                                    OTP: {b.startOtp || 'N/A'}
                                  </span>
                                </div>
                              </td>

                              {/* Client & Address */}
                              <td style={{ padding: '0.85rem 1rem' }}>
                                <div style={{ fontWeight: 700, color: 'var(--color-text-main)' }}>{b.clientName}</div>
                                <div style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.15rem' }}>
                                  <Phone size={11} />
                                  <span>+91 {b.phone}</span>
                                </div>
                                <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.1rem' }}>
                                  <MapPin size={11} />
                                  <span>{b.area} · {b.address}</span>
                                </div>
                              </td>

                              {/* Schedule */}
                              <td style={{ padding: '0.85rem 1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.45rem' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 600 }}>
                                    <Calendar size={13} style={{ color: 'var(--blishka-berry)' }} />
                                    <span>{b.preferredDate}</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setAdminRescheduleBooking(b)}
                                    style={{
                                      background: 'rgba(125, 26, 56, 0.08)',
                                      border: '1px solid rgba(125, 26, 56, 0.2)',
                                      color: 'var(--blishka-berry)',
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      borderRadius: '6px',
                                      padding: '0.15rem 0.45rem',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '0.25rem'
                                    }}
                                    title="Reschedule appointment date & slot"
                                  >
                                    <CalendarClock size={12} />
                                    <span>Reschedule</span>
                                  </button>
                                </div>
                                <div style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>
                                  {b.preferredSlot}
                                </div>
                              </td>

                              {/* Services */}
                              <td style={{ padding: '0.85rem 1rem', maxWidth: '220px' }}>
                                <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={b.items.map((i) => i.name).join(', ')}>
                                  {b.items.map((i) => i.name).join(', ')}
                                </div>
                                <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', marginTop: '0.15rem' }}>
                                  {b.items.length} services · ~{b.items.length * 25} mins
                                </div>
                              </td>

                              {/* Pay After Service */}
                              <td style={{ padding: '0.85rem 1rem' }}>
                                <strong style={{ fontSize: '0.92rem', color: 'var(--color-text-main)' }}>₹{b.totalPrice}</strong>
                                <div style={{ marginTop: '0.2rem' }}>
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      padding: '0.2rem 0.5rem',
                                      borderRadius: '999px',
                                      background:
                                        b.paymentStatus === 'Collected via Cash'
                                          ? 'rgba(46, 125, 50, 0.12)'
                                          : b.paymentStatus === 'Collected via UPI'
                                          ? 'rgba(125, 26, 56, 0.12)'
                                          : 'rgba(230, 81, 0, 0.12)',
                                      color:
                                        b.paymentStatus === 'Collected via Cash'
                                          ? '#2e7d32'
                                          : b.paymentStatus === 'Collected via UPI'
                                          ? 'var(--blishka-berry)'
                                          : '#e65100'
                                    }}
                                  >
                                    {b.paymentStatus || 'Pay After Service'}
                                  </span>
                                </div>
                              </td>

                              {/* Assign Beautician */}
                              <td style={{ padding: '0.85rem 1rem' }}>
                                <select
                                  value={b.partnerId || ''}
                                  onChange={(e) => handleAssignStaff(b.bookingRef, e.target.value)}
                                  style={{
                                    fontSize: '0.8rem',
                                    padding: '0.4rem 0.55rem',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-soft)',
                                    background: '#ffffff',
                                    maxWidth: '175px',
                                    width: '100%',
                                    fontWeight: 600
                                  }}
                                >
                                  <option value="">-- Assign Staff --</option>
                                  {staffList.map((s) => (
                                    <option key={s.id} value={s.id}>
                                      {s.name} ({s.isOnline ? 'Online' : 'Offline'})
                                    </option>
                                  ))}
                                </select>
                                {b.partnerName && (
                                  <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>
                                    Assigned: <strong>{b.partnerName}</strong>
                                  </div>
                                )}
                              </td>

                              {/* Status */}
                              <td style={{ padding: '0.85rem 1rem' }}>
                                <select
                                  value={b.status || 'Confirmed'}
                                  onChange={(e) => handleStatusChange(b.bookingRef, e.target.value)}
                                  style={{
                                    fontSize: '0.8rem',
                                    padding: '0.4rem 0.55rem',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-soft)',
                                    background: '#ffffff',
                                    fontWeight: 700
                                  }}
                                >
                                  <option value="Confirmed">Confirmed</option>
                                  <option value="Assigned">Assigned</option>
                                  <option value="In Progress">In Progress</option>
                                  <option value="Completed">Completed</option>
                                  <option value="Cancelled">Cancelled</option>
                                </select>
                              </td>

                              {/* Actions */}
                              <td style={{ padding: '0.85rem 1rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <button
                                  type="button"
                                  onClick={() => setAdminRescheduleBooking(b)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--blishka-berry)',
                                    padding: '0.35rem',
                                    borderRadius: '6px'
                                  }}
                                  title="Reschedule appointment timing"
                                >
                                  <CalendarClock size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openWhatsAppConfirmation(b)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#16a34a',
                                    padding: '0.35rem',
                                    borderRadius: '6px'
                                  }}
                                  title="Send WhatsApp appointment confirmation & start OTP to client"
                                >
                                  <MessageSquare size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => exportBookingToExcel(b)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--blishka-berry)',
                                    padding: '0.35rem',
                                    borderRadius: '6px'
                                  }}
                                  title="Download single booking Excel"
                                >
                                  <Download size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(b.bookingRef)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#d32f2f',
                                    padding: '0.35rem',
                                    borderRadius: '6px'
                                  }}
                                  title="Delete booking"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Touch-Friendly Cards View */}
                  <div className="admin-mobile-cards-container">
                    {filteredBookings.map((b) => (
                      <div
                        key={b.bookingRef}
                        style={{
                          background: '#ffffff',
                          borderRadius: '14px',
                          border: '1px solid var(--border-soft)',
                          padding: '1rem',
                          boxShadow: 'var(--shadow-sm)'
                        }}
                      >
                        {/* Header: Ref, OTP & Status Dropdown */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <strong style={{ color: 'var(--blishka-berry)', fontSize: '0.92rem' }}>{b.bookingRef}</strong>
                            {renderChannelBadge(b)}
                            <span
                              style={{
                                fontSize: '0.72rem',
                                color: '#2e7d32',
                                fontWeight: 800,
                                fontFamily: 'monospace',
                                background: 'rgba(46, 125, 50, 0.1)',
                                padding: '0.15rem 0.45rem',
                                borderRadius: '6px'
                              }}
                            >
                              OTP: {b.startOtp || 'N/A'}
                            </span>
                          </div>

                          <select
                            value={b.status || 'Confirmed'}
                            onChange={(e) => handleStatusChange(b.bookingRef, e.target.value)}
                            style={{
                              fontSize: '0.76rem',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '6px',
                              border: '1px solid var(--border-soft)',
                              fontWeight: 700,
                              background: '#ffffff'
                            }}
                          >
                            <option value="Confirmed">Confirmed</option>
                            <option value="Assigned">Assigned</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                            <option value="Cancelled">Cancelled</option>
                          </select>
                        </div>

                        {/* Client details */}
                        <div style={{ marginBottom: '0.65rem' }}>
                          <div style={{ fontWeight: 700, fontSize: '0.96rem', color: 'var(--color-text-main)' }}>
                            {b.clientName}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem', fontSize: '0.78rem', color: 'var(--color-text-muted)', flexWrap: 'wrap' }}>
                            <a
                              href={`tel:+91${b.phone}`}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'var(--blishka-berry)', textDecoration: 'none', fontWeight: 600 }}
                            >
                              <Phone size={12} />
                              <span>+91 {b.phone}</span>
                            </a>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                              <MapPin size={12} />
                              <span>{b.area} · {b.address}</span>
                            </span>
                          </div>
                        </div>

                        {/* Booking Details Box */}
                        <div style={{ background: '#fcf8f9', padding: '0.6rem 0.75rem', borderRadius: '10px', marginBottom: '0.65rem', fontSize: '0.78rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                            <span style={{ color: 'var(--color-text-muted)' }}>Schedule:</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                              <strong>{b.preferredDate} ({b.preferredSlot})</strong>
                              <button
                                type="button"
                                onClick={() => setAdminRescheduleBooking(b)}
                                style={{
                                  background: 'rgba(125, 26, 56, 0.08)',
                                  border: '1px solid rgba(125, 26, 56, 0.2)',
                                  color: 'var(--blishka-berry)',
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  borderRadius: '6px',
                                  padding: '0.12rem 0.4rem',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.2rem'
                                }}
                                title="Reschedule booking"
                              >
                                <CalendarClock size={11} />
                                <span>Edit</span>
                              </button>
                            </div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                            <span style={{ color: 'var(--color-text-muted)' }}>Services:</span>
                            <span style={{ fontWeight: 600, textAlign: 'right', maxWidth: '210px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={b.items.map((i) => i.name).join(', ')}>
                              {b.items.map((i) => i.name).join(', ')}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.35rem', borderTop: '1px dashed var(--border-soft)' }}>
                            <span style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--color-text-main)' }}>₹{b.totalPrice}</span>
                            <span
                              style={{
                                fontSize: '0.7rem',
                                padding: '0.15rem 0.5rem',
                                borderRadius: '999px',
                                fontWeight: 700,
                                background:
                                  b.paymentStatus === 'Collected via Cash'
                                    ? 'rgba(46, 125, 50, 0.12)'
                                    : b.paymentStatus === 'Collected via UPI'
                                    ? 'rgba(125, 26, 56, 0.12)'
                                    : 'rgba(230, 81, 0, 0.12)',
                                color:
                                  b.paymentStatus === 'Collected via Cash'
                                    ? '#2e7d32'
                                    : b.paymentStatus === 'Collected via UPI'
                                    ? 'var(--blishka-berry)'
                                    : '#e65100'
                              }}
                            >
                              {b.paymentStatus || 'Pay After Service'}
                            </span>
                          </div>
                        </div>

                        {/* Assign Staff Dropdown & Actions */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={{ flex: 1 }}>
                            <select
                              value={b.partnerId || ''}
                              onChange={(e) => handleAssignStaff(b.bookingRef, e.target.value)}
                              style={{
                                width: '100%',
                                fontSize: '0.78rem',
                                padding: '0.4rem 0.5rem',
                                borderRadius: '8px',
                                border: '1px solid var(--border-soft)',
                                fontWeight: 600,
                                background: '#ffffff'
                              }}
                            >
                              <option value="">-- Assign Beautician --</option>
                              {staffList.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name} ({s.isOnline ? 'Online' : 'Offline'})
                                </option>
                              ))}
                            </select>
                          </div>

                          <button
                            type="button"
                            onClick={() => setAdminRescheduleBooking(b)}
                            className="btn btn-outline btn-sm"
                            style={{ padding: '0.4rem 0.65rem', color: 'var(--blishka-berry)', borderColor: 'rgba(125, 26, 56, 0.3)' }}
                            title="Reschedule appointment timing"
                          >
                            <CalendarClock size={13} />
                          </button>

                          <button
                            type="button"
                            onClick={() => openWhatsAppConfirmation(b)}
                            className="btn btn-sm"
                            style={{
                              padding: '0.4rem 0.65rem',
                              background: 'rgba(37, 211, 102, 0.12)',
                              color: '#15803d',
                              border: '1px solid rgba(37, 211, 102, 0.3)'
                            }}
                            title="Send WhatsApp confirmation to client"
                          >
                            <MessageSquare size={13} />
                          </button>

                          <button
                            type="button"
                            onClick={() => exportBookingToExcel(b)}
                            className="btn btn-outline btn-sm"
                            style={{ padding: '0.4rem 0.65rem' }}
                            title="Download single booking Excel"
                          >
                            <Download size={13} />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(b.bookingRef)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#d32f2f',
                              padding: '0.4rem'
                            }}
                            title="Delete booking"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── TAB 2: BEAUTICIANS FLEET ── */}
        {adminTab === 'fleet' && (
          <div
            style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem', background: '#f8fafc' }}
            onScroll={handleBodyScroll}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                  <h3 style={{ margin: 0, color: 'var(--blishka-berry)', fontSize: '1.25rem', fontFamily: 'var(--font-serif)' }}>
                    Beauticians Fleet
                  </h3>
                  <span
                    style={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      padding: '0.2rem 0.6rem',
                      borderRadius: '20px',
                      background: 'rgba(46, 125, 50, 0.1)',
                      color: '#2e7d32'
                    }}
                  >
                    {staffList.filter((s) => s.isOnline).length} / {staffList.length} Active On Duty
                  </span>
                </div>
                <p style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', margin: '0.25rem 0 0' }}>
                  Verified beauty specialists, duty status, and operational performance.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsAddStaffOpen(true)}
                className="btn btn-berry btn-sm"
                style={{ gap: '0.4rem', fontSize: '0.82rem', padding: '0.5rem 1rem' }}
              >
                <Plus size={14} />
                <span>Onboard Beautician</span>
              </button>
            </div>

            {/* Beauticians Cards Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: '1rem' }}>
              {staffList.map((s) => {
                const target = s.monthlyTarget || 60;
                const completed = s.totalJobs || 0;

                return (
                  <div
                    key={s.id}
                    style={{
                      background: '#ffffff',
                      border: '1px solid var(--border-soft)',
                      borderRadius: '16px',
                      padding: '1.15rem',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div>
                      {/* Top Header: Avatar, Name, Duty Toggle */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div
                            style={{
                              width: '42px',
                              height: '42px',
                              borderRadius: '50%',
                              background: 'var(--blishka-berry)',
                              color: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 800,
                              fontSize: '1.05rem',
                              flexShrink: 0
                            }}
                          >
                            {s.name.charAt(0)}
                          </div>
                          <div>
                            <strong style={{ fontSize: '1rem', color: 'var(--color-text-main)', display: 'block' }}>{s.name}</strong>
                            <div style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>
                              PIN: <strong style={{ color: 'var(--blishka-berry)' }}>{s.pin}</strong>
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <button
                            type="button"
                            onClick={() => handleToggleStaffDuty(s.id)}
                            style={{
                              fontSize: '0.72rem',
                              padding: '0.28rem 0.65rem',
                              borderRadius: '999px',
                              border: '1px solid',
                              borderColor: s.isOnline ? '#2e7d32' : '#cbd5e1',
                              background: s.isOnline ? 'rgba(46, 125, 50, 0.1)' : '#f8fafc',
                              color: s.isOnline ? '#2e7d32' : 'var(--color-text-muted)',
                              fontWeight: 700,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.3rem'
                            }}
                          >
                            <span style={{ fontSize: '0.65rem' }}>{s.isOnline ? '🟢' : '⚪'}</span>
                            <span>{s.isOnline ? 'ON DUTY' : 'OFF DUTY'}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteStaff(s.id, s.name)}
                            title={`Remove beautician ${s.name}`}
                            style={{
                              padding: '0.32rem 0.45rem',
                              borderRadius: '8px',
                              border: '1px solid #fee2e2',
                              background: '#fff5f5',
                              color: '#dc2626',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>

                      {/* Role & Territory Pill tags */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.85rem' }}>
                        <span
                          style={{
                            fontSize: '0.74rem',
                            background: '#fdf2f4',
                            color: 'var(--blishka-berry)',
                            padding: '0.2rem 0.55rem',
                            borderRadius: '6px',
                            fontWeight: 600
                          }}
                        >
                          {s.specialty}
                        </span>
                        <span
                          style={{
                            fontSize: '0.74rem',
                            background: '#f1f5f9',
                            color: '#475569',
                            padding: '0.2rem 0.55rem',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}
                        >
                          <MapPin size={11} /> {s.area}
                        </span>
                      </div>

                      {/* 3 Informative Clean Metrics */}
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: '0.35rem',
                          background: '#f8fafc',
                          padding: '0.65rem',
                          borderRadius: '10px',
                          border: '1px solid #edf2f7',
                          marginBottom: '0.85rem'
                        }}
                      >
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                            Visits
                          </div>
                          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--color-text-main)' }}>
                            {completed}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', borderLeft: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                            Rating
                          </div>
                          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#b45309' }}>
                            ⭐ {s.rating}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                            Target
                          </div>
                          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--blishka-berry)' }}>
                            {target}/mo
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Quick Contact Buttons */}
                    <div style={{ display: 'flex', gap: '0.45rem', marginTop: '0.35rem' }}>
                      <a
                        href={`tel:+91${s.phone.replace(/\D/g, '')}`}
                        style={{
                          flex: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.35rem',
                          padding: '0.4rem 0.5rem',
                          background: '#ffffff',
                          border: '1px solid var(--border-soft)',
                          borderRadius: '8px',
                          fontSize: '0.76rem',
                          fontWeight: 600,
                          color: 'var(--color-text-main)',
                          textDecoration: 'none'
                        }}
                      >
                        <Phone size={12} color="var(--blishka-berry)" />
                        <span>Call ({s.phone.slice(-4)})</span>
                      </a>
                      <a
                        href={`https://wa.me/91${s.phone.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          flex: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.35rem',
                          padding: '0.4rem 0.5rem',
                          background: 'rgba(37, 211, 102, 0.08)',
                          border: '1px solid rgba(37, 211, 102, 0.3)',
                          borderRadius: '8px',
                          fontSize: '0.76rem',
                          fontWeight: 600,
                          color: '#15803d',
                          textDecoration: 'none'
                        }}
                      >
                        <MessageSquare size={12} />
                        <span>WhatsApp</span>
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── TAB 3: PAY AFTER SERVICE COLLECTIONS (FINANCE) ── */}
        {adminTab === 'finance' && (
          <div
            style={{ flex: 1, overflowY: 'auto', padding: '1.5rem 1.75rem', background: '#f8fafc' }}
            onScroll={handleBodyScroll}
          >
            <h3 style={{ margin: '0 0 1rem', color: 'var(--blishka-berry)', fontSize: '1.25rem', fontFamily: 'var(--font-serif)' }}>
              Pay After Service Doorstep Collections & Settlement
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ background: '#ffffff', border: '1px solid var(--border-soft)', borderRadius: '16px', padding: '1.25rem' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                  📊 Gross Bookings Value
                </span>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--color-text-main)', marginTop: '0.35rem' }}>
                  ₹{totalRevenue.toLocaleString('en-IN')}
                </div>
                <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>
                  Total of all customer appointments
                </div>
              </div>

              <div style={{ background: '#ffffff', border: '1px solid var(--border-soft)', borderRadius: '16px', padding: '1.25rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#2e7d32', textTransform: 'uppercase', fontWeight: 700 }}>
                  💵 Cash Collected at Home
                </span>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#2e7d32', marginTop: '0.35rem' }}>
                  ₹{cashCollected.toLocaleString('en-IN')}
                </div>
                <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>
                  Held by beauticians for settlement
                </div>
              </div>

              <div style={{ background: '#ffffff', border: '1px solid var(--border-soft)', borderRadius: '16px', padding: '1.25rem' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--blishka-berry)', textTransform: 'uppercase', fontWeight: 700 }}>
                  📱 UPI Direct Scans
                </span>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--blishka-berry)', marginTop: '0.35rem' }}>
                  ₹{upiCollected.toLocaleString('en-IN')}
                </div>
                <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>
                  Deposited straight to bank
                </div>
              </div>

              <div style={{ background: '#ffffff', border: '1px solid var(--border-soft)', borderRadius: '16px', padding: '1.25rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#e65100', textTransform: 'uppercase', fontWeight: 700 }}>
                  ⏳ Pending Collection
                </span>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#e65100', marginTop: '0.35rem' }}>
                  ₹{pendingCollection.toLocaleString('en-IN')}
                </div>
                <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>
                  Active visits awaiting completion
                </div>
              </div>

              <div style={{ background: '#ffffff', border: '1px solid var(--border-soft)', borderRadius: '16px', padding: '1.25rem' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                  🎁 Total Customer Savings
                </span>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--color-text-main)', marginTop: '0.35rem' }}>
                  ₹{totalSavings.toLocaleString('en-IN')}
                </div>
                <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>
                  Promotional package discounts
                </div>
              </div>
            </div>

            <div style={{ background: '#ffffff', border: '1px solid var(--border-soft)', borderRadius: '16px', padding: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem', color: 'var(--blishka-berry)', fontSize: '1.05rem' }}>
                Reconciliation & Financial Architecture
              </h4>
              <p style={{ fontSize: '0.86rem', color: 'var(--color-text-muted)', lineHeight: 1.6, margin: 0 }}>
                Under the Blishka <strong>Pay After Service</strong> model, cash collected by beauticians during doorstep visits is audited and reconciled against monthly targets and incentive disbursements, while direct UPI scans are deposited into the company account. Complete audit records are maintained with Doorstep Start OTP validation to prevent non-visit disputes.
              </p>
            </div>
          </div>
        )}

        {/* ── TAB 4: SERVICE CATALOG & DYNAMIC PRICING ── */}
        {adminTab === 'catalog' && (
          <div
            style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.75rem', background: '#f8fafc' }}
            onScroll={handleBodyScroll}
          >
            {/* Catalog Clean Control Bar */}
            <div
              style={{
                background: '#ffffff',
                border: '1px solid var(--border-soft)',
                borderRadius: '14px',
                padding: '1rem 1.25rem',
                marginBottom: '1rem',
                boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
              }}
            >
              {/* Row 1: Header Title & Main Action Buttons */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.75rem',
                  marginBottom: '0.85rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <h3 style={{ margin: 0, color: 'var(--blishka-berry)', fontSize: '1.2rem', fontFamily: 'var(--font-serif)' }}>
                    Service Catalog
                  </h3>
                  <span
                    style={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      padding: '0.15rem 0.55rem',
                      borderRadius: '20px',
                      background: 'rgba(125, 26, 56, 0.08)',
                      color: 'var(--blishka-berry)'
                    }}
                  >
                    {catalogSection === 'services'
                      ? `${servicesList.length} Services`
                      : catalogSection === 'bundles'
                      ? `${bundlesList.length} Bundles`
                      : `${categoriesList.filter((c) => c.id !== 'all' && c.id !== 'bundles').length} Categories`}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={handleManualCatalogSync}
                    disabled={isSyncingCatalog}
                    className="btn btn-outline btn-sm"
                    title="Sync catalog with Cloudflare D1 database across all systems"
                    style={{
                      fontSize: '0.78rem',
                      padding: '0.4rem 0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      color: isD1Connected ? '#15803d' : 'var(--color-text-muted)',
                      borderColor: isD1Connected ? '#bbf7d0' : undefined
                    }}
                  >
                    <RefreshCw size={13} className={isSyncingCatalog ? 'spin' : ''} />
                    <span>{isSyncingCatalog ? 'Syncing...' : isD1Connected ? 'D1 Synced' : 'Sync Catalog'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsResetCatalogOpen(true)}
                    className="btn btn-outline btn-sm"
                    title="Reset all prices and services to default factory values"
                    style={{ fontSize: '0.78rem', padding: '0.4rem 0.75rem', color: 'var(--color-text-muted)' }}
                  >
                    <RotateCcw size={13} />
                    <span>Reset Defaults</span>
                  </button>

                  {catalogSection === 'services' && (
                    <button
                      type="button"
                      onClick={handleOpenAddService}
                      className="btn btn-berry btn-sm"
                      style={{ fontSize: '0.8rem', padding: '0.45rem 0.85rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                    >
                      <Plus size={14} />
                      <span>Add Service</span>
                    </button>
                  )}
                  {catalogSection === 'bundles' && (
                    <button
                      type="button"
                      onClick={handleOpenAddBundle}
                      className="btn btn-berry btn-sm"
                      style={{ fontSize: '0.8rem', padding: '0.45rem 0.85rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                    >
                      <Plus size={14} />
                      <span>Add Bundle</span>
                    </button>
                  )}
                  {catalogSection === 'categories' && (
                    <button
                      type="button"
                      onClick={handleOpenAddCategory}
                      className="btn btn-berry btn-sm"
                      style={{ fontSize: '0.8rem', padding: '0.45rem 0.85rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                    >
                      <FolderPlus size={14} />
                      <span>Add Category</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Row 2: Segmented Switcher & Search Bar */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '0.65rem'
                }}
              >
                {/* Segmented Toggle: Services vs Bundles vs Categories */}
                <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '2px' }}>
                  <button
                    type="button"
                    onClick={() => setCatalogSection('services')}
                    style={{
                      padding: '0.35rem 0.75rem',
                      fontSize: '0.78rem',
                      fontWeight: catalogSection === 'services' ? 700 : 500,
                      borderRadius: '6px',
                      border: 'none',
                      background: catalogSection === 'services' ? '#ffffff' : 'transparent',
                      color: catalogSection === 'services' ? 'var(--blishka-berry)' : 'var(--color-text-muted)',
                      boxShadow: catalogSection === 'services' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    Services ({servicesList.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setCatalogSection('bundles')}
                    style={{
                      padding: '0.35rem 0.75rem',
                      fontSize: '0.78rem',
                      fontWeight: catalogSection === 'bundles' ? 700 : 500,
                      borderRadius: '6px',
                      border: 'none',
                      background: catalogSection === 'bundles' ? '#ffffff' : 'transparent',
                      color: catalogSection === 'bundles' ? 'var(--blishka-berry)' : 'var(--color-text-muted)',
                      boxShadow: catalogSection === 'bundles' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    Bundles ({bundlesList.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setCatalogSection('categories')}
                    style={{
                      padding: '0.35rem 0.75rem',
                      fontSize: '0.78rem',
                      fontWeight: catalogSection === 'categories' ? 700 : 500,
                      borderRadius: '6px',
                      border: 'none',
                      background: catalogSection === 'categories' ? '#ffffff' : 'transparent',
                      color: catalogSection === 'categories' ? 'var(--blishka-berry)' : 'var(--color-text-muted)',
                      boxShadow: catalogSection === 'categories' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    Categories ({categoriesList.filter((c) => c.id !== 'all' && c.id !== 'bundles').length})
                  </button>
                </div>

                {/* Search input with Clear Icon */}
                <div style={{ position: 'relative', minWidth: '220px', flex: '1', maxWidth: '320px' }}>
                  <Search
                    size={14}
                    style={{
                      position: 'absolute',
                      left: '0.65rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--color-text-muted)'
                    }}
                  />
                  <input
                    type="text"
                    placeholder={
                      catalogSection === 'services'
                        ? 'Search services...'
                        : catalogSection === 'bundles'
                        ? 'Search bundles...'
                        : 'Search categories...'
                    }
                    value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.4rem 2rem 0.4rem 2rem',
                      fontSize: '0.8rem',
                      borderRadius: '8px',
                      border: '1px solid var(--border-soft)',
                      outline: 'none',
                      background: '#ffffff'
                    }}
                  />
                  {catalogSearch && (
                    <button
                      type="button"
                      onClick={() => setCatalogSearch('')}
                      style={{
                        position: 'absolute',
                        right: '0.6rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-text-muted)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      title="Clear search"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>

              {/* Category Filter Chips (Visible when in 'services' view) */}
              {catalogSection === 'services' && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    overflowX: 'auto',
                    paddingTop: '0.65rem',
                    marginTop: '0.65rem',
                    borderTop: '1px solid #f1f5f9'
                  }}
                >
                  {categoriesList
                    .filter((cat) => cat.id !== 'bundles')
                    .map((cat) => {
                      const isSelected = catalogCategory === cat.id;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => setCatalogCategory(cat.id)}
                          style={{
                            fontSize: '0.73rem',
                            padding: '0.2rem 0.6rem',
                            borderRadius: '16px',
                            border: isSelected ? '1px solid var(--blishka-berry)' : '1px solid #e2e8f0',
                            background: isSelected ? 'var(--blishka-berry)' : '#ffffff',
                            color: isSelected ? '#ffffff' : 'var(--color-text-main)',
                            cursor: 'pointer',
                            fontWeight: isSelected ? 700 : 500,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {cat.label}
                        </button>
                      );
                    })}
                </div>
              )}
            </div>

            {/* ── SECTION A: SERVICES GRID ── */}
            {catalogSection === 'services' && (
              <>
                {servicesList.filter((s) => {
                  const matchesCat = catalogCategory === 'all' || s.category === catalogCategory;
                  const matchesSearch =
                    !catalogSearch ||
                    s.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                    (s.description && s.description.toLowerCase().includes(catalogSearch.toLowerCase())) ||
                    s.category.toLowerCase().includes(catalogSearch.toLowerCase());
                  return matchesCat && matchesSearch;
                }).length === 0 ? (
                  <div
                    style={{
                      background: '#ffffff',
                      borderRadius: '16px',
                      padding: '3rem 1.5rem',
                      textAlign: 'center',
                      border: '1px dashed var(--border-soft)'
                    }}
                  >
                    <Sparkles size={32} color="var(--blishka-gold)" style={{ margin: '0 auto 0.75rem' }} />
                    <h4 style={{ margin: '0 0 0.35rem', color: 'var(--color-text-main)' }}>No Services Found</h4>
                    <p style={{ margin: '0 0 1rem', fontSize: '0.84rem', color: 'var(--color-text-muted)' }}>
                      No services match your active search or category filter.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setCatalogSearch('');
                        setCatalogCategory('all');
                      }}
                      className="btn btn-outline btn-sm"
                    >
                      Clear Filters
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
                      gap: '0.9rem'
                    }}
                  >
                    {servicesList
                      .filter((s) => {
                        const matchesCat = catalogCategory === 'all' || s.category === catalogCategory;
                        const matchesSearch =
                          !catalogSearch ||
                          s.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                          (s.description && s.description.toLowerCase().includes(catalogSearch.toLowerCase())) ||
                          s.category.toLowerCase().includes(catalogSearch.toLowerCase());
                        return matchesCat && matchesSearch;
                      })
                      .map((s) => {
                        const discountPct = s.originalPrice > 0 ? Math.round(((s.originalPrice - s.price) / s.originalPrice) * 100) : 0;
                        const isQuickEditing = quickEditServiceId === s.id;

                        const categoryColors: Record<string, { bg: string; text: string }> = {
                          threading: { bg: '#fdf2f4', text: '#b44e66' },
                          waxing: { bg: '#fef6ee', text: '#c2410c' },
                          cleanup: { bg: '#f0fdf4', text: '#15803d' },
                          facial: { bg: '#fdf4ff', text: '#7d1a38' },
                          manicure: { bg: '#fefce8', text: '#b45309' },
                          pedicure: { bg: '#ecfeff', text: '#0e7490' },
                          hairspa: { bg: '#eef2ff', text: '#4338ca' }
                        };
                        const catBadge = categoryColors[s.category] || { bg: '#f1f5f9', text: '#475569' };

                        return (
                          <div
                            key={s.id}
                            style={{
                              background: '#ffffff',
                              border: isQuickEditing ? '1.5px solid var(--blishka-berry)' : '1px solid var(--border-soft)',
                              borderRadius: '14px',
                              padding: '1rem 1.15rem',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              boxShadow: isQuickEditing ? '0 4px 14px rgba(125, 26, 56, 0.12)' : '0 2px 5px rgba(0,0,0,0.02)',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            <div>
                              {/* Card Top: Title & Category */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.35rem' }}>
                                <strong style={{ fontSize: '0.95rem', color: 'var(--color-text-main)', lineHeight: 1.3 }}>
                                  {s.name}
                                </strong>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}>
                                  <span
                                    style={{
                                      fontSize: '0.68rem',
                                      fontWeight: 700,
                                      background: '#fdf2f4',
                                      color: 'var(--blishka-berry)',
                                      padding: '0.15rem 0.45rem',
                                      borderRadius: '6px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '3px'
                                    }}
                                    title="Service Duration"
                                  >
                                    <Clock size={10} />
                                    {formatDuration(s.durationMinutes)}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: '0.68rem',
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.04em',
                                      background: catBadge.bg,
                                      color: catBadge.text,
                                      padding: '0.15rem 0.5rem',
                                      borderRadius: '6px'
                                    }}
                                  >
                                    {s.category}
                                  </span>
                                </div>
                              </div>

                              {/* Description */}
                              <p
                                style={{
                                  fontSize: '0.78rem',
                                  color: 'var(--color-text-muted)',
                                  margin: '0 0 0.85rem',
                                  lineHeight: 1.4,
                                  minHeight: '1.2rem'
                                }}
                              >
                                {s.description || 'Doorstep parlour grooming service'}
                              </p>
                            </div>

                            {/* Pricing & Editing Controls */}
                            <div
                              style={{
                                background: '#f8fafc',
                                borderRadius: '10px',
                                padding: '0.7rem 0.85rem',
                                border: '1px solid #edf2f7'
                              }}
                            >
                              {isQuickEditing ? (
                                <div>
                                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--blishka-berry)', marginBottom: '0.35rem' }}>
                                    Update Doorstep Pricing:
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.5rem' }}>
                                    <div style={{ flex: 1 }}>
                                      <label style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', display: 'block' }}>Selling (₹)</label>
                                      <input
                                        type="number"
                                        value={quickPriceInput}
                                        onChange={(e) => setQuickPriceInput(e.target.value)}
                                        style={{
                                          width: '100%',
                                          padding: '0.3rem 0.45rem',
                                          fontSize: '0.88rem',
                                          fontWeight: 700,
                                          borderRadius: '6px',
                                          border: '1px solid var(--blishka-berry)',
                                          outline: 'none'
                                        }}
                                        autoFocus
                                      />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <label style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', display: 'block' }}>MRP / Orig (₹)</label>
                                      <input
                                        type="number"
                                        value={quickOrigPriceInput}
                                        onChange={(e) => setQuickOrigPriceInput(e.target.value)}
                                        style={{
                                          width: '100%',
                                          padding: '0.3rem 0.45rem',
                                          fontSize: '0.88rem',
                                          borderRadius: '6px',
                                          border: '1px solid #cbd5e1',
                                          outline: 'none'
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                                    <button
                                      type="button"
                                      onClick={() => handleSaveQuickPrice(s.id)}
                                      className="btn btn-berry btn-sm"
                                      style={{ flex: 1, padding: '0.3rem', fontSize: '0.76rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}
                                    >
                                      <Check size={13} />
                                      <span>Save Price</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setQuickEditServiceId(null)}
                                      className="btn btn-outline btn-sm"
                                      style={{ flex: 1, padding: '0.3rem', fontSize: '0.76rem' }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.45rem' }}>
                                      <span style={{ fontWeight: 800, color: 'var(--blishka-berry)', fontSize: '1.15rem' }}>
                                        ₹{s.price}
                                      </span>
                                      <span style={{ fontSize: '0.78rem', textDecoration: 'line-through', color: 'var(--color-text-muted)' }}>
                                        ₹{s.originalPrice}
                                      </span>
                                      {discountPct > 0 && (
                                        <span
                                          style={{
                                            fontSize: '0.66rem',
                                            fontWeight: 700,
                                            color: '#15803d',
                                            background: '#dcfce7',
                                            padding: '0.1rem 0.35rem',
                                            borderRadius: '4px'
                                          }}
                                        >
                                          {discountPct}% OFF
                                        </span>
                                      )}
                                    </div>
                                    {s.displayPrice && (
                                      <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', display: 'block', marginTop: '0.1rem' }}>
                                        Label: {s.displayPrice}
                                      </span>
                                    )}
                                  </div>

                                  {/* Action Buttons */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                    <button
                                      type="button"
                                      onClick={() => handleStartQuickPrice(s)}
                                      title="Quick Price Update"
                                      style={{
                                        background: '#ffffff',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        padding: '0.35rem 0.5rem',
                                        fontSize: '0.75rem',
                                        color: 'var(--color-text-main)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.2rem',
                                        fontWeight: 600
                                      }}
                                    >
                                      <IndianRupee size={12} />
                                      <span>Price</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => handleOpenEditService(s)}
                                      title="Edit Full Service Details"
                                      style={{
                                        background: '#ffffff',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '6px',
                                        padding: '0.35rem 0.5rem',
                                        color: 'var(--blishka-berry)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Edit2 size={13} />
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => setDeleteTarget({ type: 'service', id: s.id, name: s.name })}
                                      title="Delete Service"
                                      style={{
                                        background: '#ffffff',
                                        border: '1px solid #fee2e2',
                                        borderRadius: '6px',
                                        padding: '0.35rem 0.5rem',
                                        color: '#ef4444',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </>
            )}

            {/* ── SECTION B: BUNDLES GRID ── */}
            {catalogSection === 'bundles' && (
              <>
                {bundlesList.filter((b) => {
                  if (!catalogSearch) return true;
                  return (
                    b.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                    b.servicesIncluded.some((item) => item.toLowerCase().includes(catalogSearch.toLowerCase()))
                  );
                }).length === 0 ? (
                  <div
                    style={{
                      background: '#ffffff',
                      borderRadius: '16px',
                      padding: '3rem 1.5rem',
                      textAlign: 'center',
                      border: '1px dashed var(--border-soft)'
                    }}
                  >
                    <Gift size={32} color="var(--blishka-gold)" style={{ margin: '0 auto 0.75rem' }} />
                    <h4 style={{ margin: '0 0 0.35rem', color: 'var(--color-text-main)' }}>No Bundles Found</h4>
                    <p style={{ margin: '0 0 1rem', fontSize: '0.84rem', color: 'var(--color-text-muted)' }}>
                      No curated packages match your search query.
                    </p>
                    <button type="button" onClick={() => setCatalogSearch('')} className="btn btn-outline btn-sm">
                      Clear Search
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                      gap: '0.9rem'
                    }}
                  >
                    {bundlesList
                      .filter((b) => {
                        if (!catalogSearch) return true;
                        return (
                          b.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                          b.servicesIncluded.some((item) => item.toLowerCase().includes(catalogSearch.toLowerCase()))
                        );
                      })
                      .map((b) => (
                        <div
                          key={b.id}
                          style={{
                            background: '#ffffff',
                            border: '1px solid var(--border-soft)',
                            borderRadius: '14px',
                            padding: '1.15rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                          }}
                        >
                          <div>
                            {/* Bundle Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.65rem' }}>
                              <div>
                                <strong style={{ fontSize: '1rem', color: 'var(--color-text-main)', display: 'block' }}>
                                  {b.name}
                                </strong>
                                <span style={{ fontSize: '0.72rem', color: 'var(--blishka-berry)', fontWeight: 600 }}>
                                  Save ₹{b.saveAmount} combo discount
                                </span>
                              </div>
                              <span
                                style={{
                                  fontSize: '0.72rem',
                                  padding: '0.2rem 0.55rem',
                                  borderRadius: '12px',
                                  background: b.tagColor ? `${b.tagColor}18` : '#fdf2f4',
                                  color: b.tagColor || 'var(--blishka-berry)',
                                  fontWeight: 700
                                }}
                              >
                                {b.iconType.toUpperCase()}
                              </span>
                            </div>

                            {/* Included Services Tags */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '1rem' }}>
                              {b.servicesIncluded.map((sName, idx) => (
                                <span
                                  key={idx}
                                  style={{
                                    fontSize: '0.72rem',
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    padding: '0.2rem 0.45rem',
                                    borderRadius: '6px',
                                    color: 'var(--color-text-main)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem'
                                  }}
                                >
                                  <Check size={11} color="var(--blishka-berry)" />
                                  <span>{sName}</span>
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Bundle Pricing & Actions */}
                          <div
                            style={{
                              background: '#f8fafc',
                              borderRadius: '10px',
                              padding: '0.75rem 0.85rem',
                              border: '1px solid #edf2f7',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <div>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.45rem' }}>
                                <span style={{ fontWeight: 800, color: 'var(--blishka-berry)', fontSize: '1.18rem' }}>
                                  ₹{b.price}
                                </span>
                                <span style={{ fontSize: '0.78rem', textDecoration: 'line-through', color: 'var(--color-text-muted)' }}>
                                  ₹{b.originalValue}
                                </span>
                              </div>
                              <span style={{ fontSize: '0.7rem', color: '#15803d', fontWeight: 600 }}>
                                Value ₹{b.originalValue}
                              </span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                              <button
                                type="button"
                                onClick={() => handleOpenEditBundle(b)}
                                title="Edit Bundle Details & Pricing"
                                style={{
                                  background: '#ffffff',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '6px',
                                  padding: '0.4rem 0.65rem',
                                  color: 'var(--blishka-berry)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.3rem',
                                  fontSize: '0.76rem',
                                  fontWeight: 600
                                }}
                              >
                                <Edit2 size={13} />
                                <span>Edit</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => setDeleteTarget({ type: 'bundle', id: b.id, name: b.name })}
                                title="Delete Bundle"
                                style={{
                                  background: '#ffffff',
                                  border: '1px solid #fee2e2',
                                  borderRadius: '6px',
                                  padding: '0.4rem 0.55rem',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </>
            )}

            {/* ── SECTION C: CATEGORIES GRID ── */}
            {catalogSection === 'categories' && (
              <div>
                {categoriesList.filter((cat) => {
                  if (!catalogSearch) return cat.id !== 'all';
                  return (
                    cat.id !== 'all' &&
                    (cat.label.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                      cat.tagline.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                      cat.id.toLowerCase().includes(catalogSearch.toLowerCase()))
                  );
                }).length === 0 ? (
                  <div
                    style={{
                      background: '#ffffff',
                      borderRadius: '16px',
                      padding: '3rem 1.5rem',
                      textAlign: 'center',
                      border: '1px dashed var(--border-soft)'
                    }}
                  >
                    <FolderPlus size={32} color="var(--blishka-gold)" style={{ margin: '0 auto 0.75rem' }} />
                    <h4 style={{ margin: '0 0 0.35rem', color: 'var(--color-text-main)' }}>No Categories Found</h4>
                    <p style={{ margin: '0 0 1rem', fontSize: '0.84rem', color: 'var(--color-text-muted)' }}>
                      No categories match your search query.
                    </p>
                    <button type="button" onClick={() => setCatalogSearch('')} className="btn btn-outline btn-sm">
                      Clear Search
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
                      gap: '0.9rem'
                    }}
                  >
                    {categoriesList
                      .filter((cat) => {
                        if (!catalogSearch) return cat.id !== 'all';
                        return (
                          cat.id !== 'all' &&
                          (cat.label.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                            cat.tagline.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                            cat.id.toLowerCase().includes(catalogSearch.toLowerCase()))
                        );
                      })
                      .map((cat) => {
                        const srvCount = servicesList.filter((s) => s.category === cat.id).length;
                        const isSystem = cat.id === 'bundles';

                        return (
                          <div
                            key={cat.id}
                            style={{
                              background: '#ffffff',
                              border: '1px solid var(--border-soft)',
                              borderRadius: '14px',
                              padding: '1.15rem',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                            }}
                          >
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                <div>
                                  <strong style={{ fontSize: '1rem', color: 'var(--color-text-main)', display: 'block' }}>
                                    {cat.label}
                                  </strong>
                                  <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                                    ID: #{cat.id}
                                  </span>
                                </div>
                                {isSystem ? (
                                  <span
                                    style={{
                                      fontSize: '0.68rem',
                                      fontWeight: 700,
                                      background: '#f1f5f9',
                                      color: 'var(--color-text-muted)',
                                      padding: '0.15rem 0.5rem',
                                      borderRadius: '6px'
                                    }}
                                  >
                                    SYSTEM
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      fontSize: '0.68rem',
                                      fontWeight: 700,
                                      background: 'rgba(125, 26, 56, 0.08)',
                                      color: 'var(--blishka-berry)',
                                      padding: '0.15rem 0.5rem',
                                      borderRadius: '6px'
                                    }}
                                  >
                                    ACTIVE
                                  </span>
                                )}
                              </div>

                              <p
                                style={{
                                  fontSize: '0.8rem',
                                  color: 'var(--color-text-muted)',
                                  margin: '0 0 0.85rem',
                                  lineHeight: 1.4
                                }}
                              >
                                {cat.tagline}
                              </p>

                              {cat.image && (
                                <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: '0.85rem' }}>
                                  <strong>Image:</strong> {cat.image}
                                </div>
                              )}
                            </div>

                            <div
                              style={{
                                background: '#f8fafc',
                                borderRadius: '10px',
                                padding: '0.65rem 0.85rem',
                                border: '1px solid #edf2f7',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <span style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--color-text-main)' }}>
                                {cat.id === 'bundles' ? `${bundlesList.length} Bundles` : `${srvCount} Services`}
                              </span>

                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                <button
                                  type="button"
                                  onClick={() => handleOpenEditCategory(cat)}
                                  title="Edit Category"
                                  style={{
                                    background: '#ffffff',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    padding: '0.35rem 0.55rem',
                                    color: 'var(--blishka-berry)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    fontSize: '0.75rem',
                                    fontWeight: 600
                                  }}
                                >
                                  <Edit2 size={12} />
                                  <span>Edit</span>
                                </button>

                                {!isSystem && (
                                  <button
                                    type="button"
                                    onClick={() => setDeleteTarget({ type: 'category', id: cat.id, name: cat.label })}
                                    title="Delete Category"
                                    style={{
                                      background: '#ffffff',
                                      border: '1px solid #fee2e2',
                                      borderRadius: '6px',
                                      padding: '0.35rem 0.55rem',
                                      color: '#ef4444',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── TAB 5: OPERATIONAL AREAS & REGIONAL COVERAGE (SUPER ADMIN ONLY) ── */}
        {adminTab === 'areas' && currentRole === 'super_admin' && (
          <div
            style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.75rem', background: '#f8fafc' }}
            onScroll={handleBodyScroll}
          >
            {/* Header & KPI Metrics Bar */}
            <div
              style={{
                background: '#ffffff',
                border: '1px solid var(--border-soft)',
                borderRadius: '14px',
                padding: '1.25rem',
                marginBottom: '1rem',
                boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
              }}
            >
              {/* Row 1: Title, Role Badge, and Actions */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0, color: 'var(--blishka-berry)', fontSize: '1.2rem', fontFamily: 'var(--font-serif)' }}>
                      Regional Operational Areas
                    </h3>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        padding: '0.15rem 0.55rem',
                        borderRadius: '999px',
                        background: 'linear-gradient(135deg, rgba(184,134,11,0.12), rgba(212,175,55,0.2))',
                        color: '#b8860b',
                        border: '1px solid rgba(184,134,11,0.3)'
                      }}
                    >
                      <Crown size={11} />
                      <span>Super Admin Exclusive</span>
                    </span>
                  </div>
                  <p style={{ margin: '0.3rem 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    Add, edit, toggle, or remove serviceable delivery colonies and sectors across Bhilai & Durg.
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleSyncAreasManual}
                    disabled={isSyncingAreas}
                    className="btn btn-outline btn-sm"
                    title="Sync operational areas with Cloudflare D1 database"
                    style={{
                      fontSize: '0.78rem',
                      padding: '0.4rem 0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      color: isD1AreasConnected ? '#15803d' : 'var(--color-text-muted)',
                      borderColor: isD1AreasConnected ? '#bbf7d0' : undefined
                    }}
                  >
                    <RefreshCw size={13} className={isSyncingAreas ? 'spin-icon' : ''} />
                    <span>{isSyncingAreas ? 'Syncing...' : isD1AreasConnected ? 'D1 Synced' : 'Sync Areas'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsResetAreasOpen(true)}
                    className="btn btn-outline btn-sm"
                    title="Restore the 17 default factory Bhilai locations"
                    style={{ fontSize: '0.78rem', padding: '0.4rem 0.75rem', color: 'var(--color-text-muted)' }}
                  >
                    <RotateCcw size={13} />
                    <span>Reset Defaults</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleOpenAddArea}
                    className="btn btn-berry btn-sm"
                    style={{ fontSize: '0.8rem', padding: '0.45rem 0.85rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                  >
                    <Plus size={14} />
                    <span>Add Operational Area</span>
                  </button>
                </div>
              </div>

              {/* Row 2: Live Statistics Strip */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '0.75rem',
                  paddingTop: '0.85rem',
                  borderTop: '1px solid #f1f5f9'
                }}
              >
                <div style={{ background: '#fdfcfc', padding: '0.65rem 0.85rem', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                    Total Defined Areas
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--color-text-main)', marginTop: '0.15rem' }}>
                    {operationalAreas.length}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)' }}>Locations in system</div>
                </div>

                <div style={{ background: 'rgba(46,125,50,0.04)', padding: '0.65rem 0.85rem', borderRadius: '10px', border: '1px solid rgba(46,125,50,0.18)' }}>
                  <div style={{ fontSize: '0.7rem', color: '#2e7d32', textTransform: 'uppercase', fontWeight: 700 }}>
                    Active Delivery Zones
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#2e7d32', marginTop: '0.15rem' }}>
                    {operationalAreas.filter((a) => a.isServiceable).length}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#2e7d32' }}>Customers can book now</div>
                </div>

                <div style={{ background: 'rgba(184,134,11,0.04)', padding: '0.65rem 0.85rem', borderRadius: '10px', border: '1px solid rgba(184,134,11,0.18)' }}>
                  <div style={{ fontSize: '0.7rem', color: '#b8860b', textTransform: 'uppercase', fontWeight: 700 }}>
                    Coming Soon Zones
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#b8860b', marginTop: '0.15rem' }}>
                    {operationalAreas.filter((a) => !a.isServiceable).length}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#b8860b' }}>Waitlist lead capture active</div>
                </div>

                <div style={{ background: 'rgba(125,26,56,0.03)', padding: '0.65rem 0.85rem', borderRadius: '10px', border: '1px solid rgba(125,26,56,0.12)' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--blishka-berry)', textTransform: 'uppercase', fontWeight: 700 }}>
                    Primary Operational Hub
                  </div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--blishka-berry)', marginTop: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    Hari Raj Restaurant
                  </div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)' }}>5.0 km Max Dispatch Radius</div>
                </div>
              </div>
            </div>

            {/* Filter & Search Bar */}
            <div
              style={{
                background: '#ffffff',
                border: '1px solid var(--border-soft)',
                borderRadius: '12px',
                padding: '0.75rem 1rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '0.65rem'
              }}
            >
              {/* Filter Pills */}
              <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '2px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setAreaStatusFilter('all')}
                  style={{
                    padding: '0.35rem 0.75rem',
                    fontSize: '0.78rem',
                    fontWeight: areaStatusFilter === 'all' ? 700 : 500,
                    borderRadius: '6px',
                    border: 'none',
                    background: areaStatusFilter === 'all' ? '#ffffff' : 'transparent',
                    color: areaStatusFilter === 'all' ? 'var(--blishka-berry)' : 'var(--color-text-muted)',
                    boxShadow: areaStatusFilter === 'all' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  All ({operationalAreas.length})
                </button>
                <button
                  type="button"
                  onClick={() => setAreaStatusFilter('serviceable')}
                  style={{
                    padding: '0.35rem 0.75rem',
                    fontSize: '0.78rem',
                    fontWeight: areaStatusFilter === 'serviceable' ? 700 : 500,
                    borderRadius: '6px',
                    border: 'none',
                    background: areaStatusFilter === 'serviceable' ? '#ffffff' : 'transparent',
                    color: areaStatusFilter === 'serviceable' ? '#2e7d32' : 'var(--color-text-muted)',
                    boxShadow: areaStatusFilter === 'serviceable' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  Active ({operationalAreas.filter((a) => a.isServiceable).length})
                </button>
                <button
                  type="button"
                  onClick={() => setAreaStatusFilter('coming_soon')}
                  style={{
                    padding: '0.35rem 0.75rem',
                    fontSize: '0.78rem',
                    fontWeight: areaStatusFilter === 'coming_soon' ? 700 : 500,
                    borderRadius: '6px',
                    border: 'none',
                    background: areaStatusFilter === 'coming_soon' ? '#ffffff' : 'transparent',
                    color: areaStatusFilter === 'coming_soon' ? '#b8860b' : 'var(--color-text-muted)',
                    boxShadow: areaStatusFilter === 'coming_soon' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  Coming Soon ({operationalAreas.filter((a) => !a.isServiceable).length})
                </button>
              </div>

              {/* Search Box */}
              <div style={{ position: 'relative', minWidth: '220px', flex: '1', maxWidth: '300px' }}>
                <Search
                  size={14}
                  style={{
                    position: 'absolute',
                    left: '0.65rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--color-text-muted)'
                  }}
                />
                <input
                  type="text"
                  placeholder="Search operational area..."
                  value={areaSearch}
                  onChange={(e) => setAreaSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.4rem 2rem 0.4rem 2rem',
                    fontSize: '0.8rem',
                    borderRadius: '8px',
                    border: '1px solid var(--border-soft)',
                    outline: 'none',
                    background: '#ffffff'
                  }}
                />
                {areaSearch && (
                  <button
                    type="button"
                    onClick={() => setAreaSearch('')}
                    style={{
                      position: 'absolute',
                      right: '0.6rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-text-muted)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title="Clear search"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>

            {/* Areas Cards Grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
                gap: '0.85rem'
              }}
            >
              {operationalAreas
                .filter((item) => {
                  const matchesFilter =
                    areaStatusFilter === 'all' ||
                    (areaStatusFilter === 'serviceable' && item.isServiceable) ||
                    (areaStatusFilter === 'coming_soon' && !item.isServiceable);
                  const matchesSearch =
                    !areaSearch ||
                    item.name.toLowerCase().includes(areaSearch.toLowerCase()) ||
                    (item.city && item.city.toLowerCase().includes(areaSearch.toLowerCase()));
                  return matchesFilter && matchesSearch;
                })
                .map((item) => {
                  const hasCoords = item.lat !== undefined && item.lng !== undefined;
                  const distFromHub = hasCoords
                    ? calculateHaversineDistance(item.lat!, item.lng!, HARI_RAJ_HUB.lat, HARI_RAJ_HUB.lng)
                    : null;
                  const isWithinHubRadius = distFromHub !== null && distFromHub <= HARI_RAJ_HUB.radiusKm;

                  return (
                    <div
                      key={item.id}
                      style={{
                        background: '#ffffff',
                        border: item.isServiceable ? '1px solid rgba(46,125,50,0.22)' : '1px solid var(--border-soft)',
                        borderRadius: '12px',
                        padding: '1rem',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        position: 'relative',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                      }}
                    >
                      <div>
                        {/* Card Top: Area Name & Service Badge */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                              <MapPin size={15} style={{ color: item.isServiceable ? '#2e7d32' : 'var(--blishka-gold)', flexShrink: 0 }} />
                              <h4 style={{ margin: 0, fontSize: '0.96rem', fontWeight: 700, color: 'var(--color-text-main)' }}>
                                {item.name}
                              </h4>
                            </div>
                            <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginLeft: '1.25rem' }}>
                              {item.city || 'Bhilai'}, Chhattisgarh
                            </span>
                          </div>

                          {item.isServiceable ? (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                padding: '0.15rem 0.5rem',
                                borderRadius: '999px',
                                background: '#dcfce7',
                                color: '#15803d',
                                border: '1px solid #bbf7d0',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              <CheckCircle2 size={11} />
                              <span>Active</span>
                            </span>
                          ) : (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                padding: '0.15rem 0.5rem',
                                borderRadius: '999px',
                                background: '#fef3c7',
                                color: '#b45309',
                                border: '1px solid #fde68a',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              <Clock size={11} />
                              <span>Coming Soon</span>
                            </span>
                          )}
                        </div>

                        {/* Hub & Distance Info */}
                        <div
                          style={{
                            background: '#f8fafc',
                            borderRadius: '8px',
                            padding: '0.45rem 0.65rem',
                            fontSize: '0.72rem',
                            color: 'var(--color-text-muted)',
                            margin: '0.5rem 0',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.25rem'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Distance to Hari Raj Hub:</span>
                            {distFromHub !== null ? (
                              <span
                                style={{
                                  fontWeight: 700,
                                  color: isWithinHubRadius ? '#2e7d32' : '#b45309'
                                }}
                              >
                                {distFromHub} km {isWithinHubRadius ? '(Within 5km)' : '(Beyond 5km)'}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Not pinned</span>
                            )}
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Coordinates:</span>
                            {hasCoords ? (
                              <a
                                href={`https://www.google.com/maps?q=${item.lat},${item.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: 'var(--blishka-berry)',
                                  textDecoration: 'none',
                                  fontWeight: 600,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.2rem'
                                }}
                                title="Open Google Maps Pin"
                              >
                                <span>{item.lat?.toFixed(4)}, {item.lng?.toFixed(4)}</span>
                                <span style={{ fontSize: '0.65rem' }}>↗</span>
                              </a>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>None</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Card Bottom Controls */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.5rem',
                          paddingTop: '0.65rem',
                          borderTop: '1px solid #f1f5f9',
                          marginTop: '0.4rem'
                        }}
                      >
                        {/* Quick Toggle Service Button */}
                        <button
                          type="button"
                          onClick={() => handleToggleAreaServiceable(item)}
                          className="btn btn-outline btn-sm"
                          style={{
                            fontSize: '0.72rem',
                            padding: '0.25rem 0.55rem',
                            color: item.isServiceable ? '#b45309' : '#15803d',
                            borderColor: item.isServiceable ? '#fde68a' : '#bbf7d0',
                            background: item.isServiceable ? '#fffbeb' : '#f0fdf4'
                          }}
                          title={item.isServiceable ? 'Disable delivery for this area' : 'Enable delivery for this area'}
                        >
                          {item.isServiceable ? 'Mark Coming Soon' : 'Activate Delivery'}
                        </button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenEditArea(item)}
                            className="btn btn-outline btn-sm"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.72rem' }}
                            title="Edit area name and coordinates"
                          >
                            <Edit2 size={12} />
                            <span>Edit</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setAreaToDelete(item)}
                            className="btn btn-outline btn-sm"
                            style={{
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.72rem',
                              color: '#dc2626',
                              borderColor: '#fecaca'
                            }}
                            title="Remove operational area"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* ── 5. REVIEWS & RATINGS MANAGEMENT TAB (Super Admin Only) ── */}
        {adminTab === 'reviews' && currentRole === 'super_admin' && (
          <div style={{ padding: '1rem', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Top Toolbar */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '1rem',
                marginBottom: '1.25rem',
                background: '#ffffff',
                padding: '1rem 1.25rem',
                borderRadius: '16px',
                border: '1px solid rgba(125, 26, 56, 0.12)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Star size={20} fill="#f59e0b" color="#f59e0b" />
                  <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: 'var(--blishka-berry)' }}>
                    Customer Reviews & Ratings Management
                  </h3>
                </div>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  Curate verified customer ratings & testimonials displayed below services on the homepage.
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    syncReviewsFromRemote().then(res => {
                      if (res.success) {
                        setReviewsList(res.reviews);
                        showToast('Reviews refreshed from D1 database');
                      }
                    });
                  }}
                  className="btn btn-outline btn-sm"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', borderRadius: '10px' }}
                >
                  <RefreshCw size={14} />
                  <span>Refresh</span>
                </button>

                <button
                  type="button"
                  onClick={() => setIsAddReviewModalOpen(true)}
                  className="btn btn-berry btn-sm"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', borderRadius: '10px', fontWeight: 700 }}
                >
                  <Plus size={16} />
                  <span>Add Review</span>
                </button>
              </div>
            </div>

            {/* Reviews Count & Rating Stats Strip */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}
            >
              <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                  Active Reviews
                </span>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--blishka-berry)', marginTop: '0.2rem' }}>
                  {reviewsList.length}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#15803d', fontWeight: 600 }}>
                  Featured on Homepage
                </div>
              </div>

              <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                  Average Rating
                </span>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#f59e0b', marginTop: '0.2rem' }}>
                  ★ {reviewsList.length > 0 ? (reviewsList.reduce((acc, r) => acc + r.rating, 0) / reviewsList.length).toFixed(1) : '5.0'} / 5.0
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                  Customer Satisfaction
                </div>
              </div>

              <div style={{ background: '#ffffff', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
                  Verified Doorstep Clients
                </span>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#2e7d32', marginTop: '0.2rem' }}>
                  100%
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                  Bhilai & Durg Residents
                </div>
              </div>
            </div>

            {/* Reviews Cards List */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem' }}>
              {reviewsList.map((rev) => (
                <div
                  key={rev.id}
                  style={{
                    background: '#ffffff',
                    border: '1px solid rgba(125, 26, 56, 0.15)',
                    borderRadius: '14px',
                    padding: '1.25rem',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--color-text-title)' }}>
                          {rev.customerName}
                        </span>
                        <span style={{ color: '#2e7d32', display: 'inline-flex' }} title="Verified">
                          <CheckCircle2 size={14} />
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '2px', color: '#f59e0b' }}>
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} size={14} fill={s <= rev.rating ? '#f59e0b' : 'none'} color="#f59e0b" />
                        ))}
                      </div>
                    </div>

                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {rev.area && <span>📍 {rev.area}</span>}
                      {rev.bookingRef && <span>Ref: {rev.bookingRef}</span>}
                    </div>

                    {rev.serviceNames && (
                      <div style={{
                        display: 'inline-block',
                        background: 'rgba(125, 26, 56, 0.06)',
                        color: 'var(--blishka-berry)',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        padding: '0.15rem 0.5rem',
                        borderRadius: '6px',
                        marginBottom: '0.65rem'
                      }}>
                        ✨ {rev.serviceNames}
                      </div>
                    )}

                    <p style={{
                      margin: 0,
                      fontSize: '0.84rem',
                      color: 'var(--color-text-body)',
                      lineHeight: 1.5,
                      fontStyle: 'italic'
                    }}>
                      "{rev.reviewText}"
                    </p>
                  </div>

                  <div style={{
                    marginTop: '1rem',
                    paddingTop: '0.65rem',
                    borderTop: '1px dashed rgba(0,0,0,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                      {rev.createdAt ? new Date(rev.createdAt).toLocaleDateString('en-IN') : 'Verified'}
                    </span>

                    <button
                      type="button"
                      onClick={() => setReviewToDelete(rev)}
                      className="btn btn-outline btn-sm"
                      style={{
                        padding: '0.2rem 0.5rem',
                        fontSize: '0.72rem',
                        color: '#dc2626',
                        borderColor: '#fecaca',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                      title="Delete review"
                    >
                      <Trash2 size={12} />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* ── ADD BEAUTICIAN MODAL ── */}
      {isAddStaffOpen && (
        <div className="modal-overlay" style={{ zIndex: 120000 }} onClick={() => setIsAddStaffOpen(false)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '440px', padding: '2rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.4rem', color: 'var(--blishka-berry)', fontFamily: 'var(--font-serif)' }}>
              Onboard Beauty Professional
            </h3>
            <p style={{ fontSize: '0.84rem', color: 'var(--color-text-muted)', marginBottom: '1.25rem' }}>
              Register a new beautician into the Blishka doorstep dispatch fleet.
            </p>

            <form onSubmit={handleAddStaffSubmit}>
              <div className="form-group" style={{ marginBottom: '0.85rem' }}>
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  required
                  value={newStaffName}
                  onChange={(e) => setNewStaffName(e.target.value)}
                  placeholder="e.g. Rekha Sahu"
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '0.85rem' }}>
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  required
                  maxLength={10}
                  value={newStaffPhone}
                  onChange={(e) => setNewStaffPhone(e.target.value.replace(/\D/g, ''))}
                  placeholder="10-digit mobile"
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '0.85rem' }}>
                <label className="form-label">Specialty / Role</label>
                <input
                  type="text"
                  value={newStaffSpecialty}
                  onChange={(e) => setNewStaffSpecialty(e.target.value)}
                  placeholder="e.g. Facial & Waxing Specialist"
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '0.85rem' }}>
                <label className="form-label">Operating Territory</label>
                <input
                  type="text"
                  value={newStaffArea}
                  onChange={(e) => setNewStaffArea(e.target.value)}
                  placeholder="e.g. Bhilai - Nehru Nagar & Smriti Nagar"
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                <label className="form-label">Monthly Target Visits (Optional)</label>
                <input
                  type="number"
                  value={newStaffTarget}
                  onChange={(e) => setNewStaffTarget(Number(e.target.value))}
                  className="form-input"
                />
                <small style={{ color: 'var(--color-text-muted)', fontSize: '0.72rem', marginTop: '0.25rem', display: 'block' }}>
                  Optional operational benchmark for internal tracking.
                </small>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setIsAddStaffOpen(false)}
                  className="btn btn-outline"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-berry" style={{ flex: 1 }}>
                  Save Beautician
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT SERVICE MODAL ── */}
      {isServiceModalOpen && (
        <div className="modal-overlay" style={{ zIndex: 120000 }} onClick={() => setIsServiceModalOpen(false)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '460px', padding: '1.75rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
              <h3 style={{ margin: 0, color: 'var(--blishka-berry)', fontFamily: 'var(--font-serif)', fontSize: '1.2rem' }}>
                {serviceToEdit ? 'Edit Service & Pricing' : 'Add New Doorstep Service'}
              </h3>
              <button
                type="button"
                onClick={() => setIsServiceModalOpen(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '1.25rem' }}>
              {serviceToEdit
                ? 'Update rates, service title, category, or customer description.'
                : 'Introduce a new treatment to the customer-facing Blishka menu.'}
            </p>

            <form onSubmit={handleSaveService}>
              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label className="form-label">Service Name *</label>
                <input
                  type="text"
                  required
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  placeholder="e.g. Herbal Radiance Facial"
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <label className="form-label" style={{ margin: 0 }}>Treatment Category *</label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsServiceModalOpen(false);
                      handleOpenAddCategory();
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--blishka-berry)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0
                    }}
                  >
                    + New Category
                  </button>
                </div>
                <select
                  value={serviceCategory}
                  onChange={(e) => setServiceCategory(e.target.value)}
                  className="form-input"
                  style={{ background: '#ffffff' }}
                >
                  {categoriesList
                    .filter((cat) => cat.id !== 'all' && cat.id !== 'bundles')
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.label}
                      </option>
                    ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div className="form-group" style={{ flex: 1, margin: 0 }}>
                  <label className="form-label">Selling Price (₹) *</label>
                  <input
                    type="number"
                    required
                    min={0}
                    value={servicePrice}
                    onChange={(e) => setServicePrice(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="e.g. 599"
                    className="form-input"
                  />
                </div>
                <div className="form-group" style={{ flex: 1, margin: 0 }}>
                  <label className="form-label">MRP / Original (₹)</label>
                  <input
                    type="number"
                    min={0}
                    value={serviceOriginalPrice}
                    onChange={(e) => setServiceOriginalPrice(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="e.g. 849"
                    className="form-input"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div className="form-group" style={{ flex: 1, margin: 0 }}>
                  <label className="form-label">
                    <Clock size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                    Duration (Minutes) *
                  </label>
                  <input
                    type="number"
                    required
                    min={5}
                    step={5}
                    value={serviceDurationMinutes}
                    onChange={(e) => setServiceDurationMinutes(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="e.g. 30"
                    className="form-input"
                  />
                  <small style={{ color: 'var(--color-text-muted)', fontSize: '0.7rem', display: 'block', marginTop: '2px' }}>
                    Preview: {formatDuration(Number(serviceDurationMinutes) || 30)}
                  </small>
                </div>
                <div className="form-group" style={{ flex: 1, margin: 0 }}>
                  <label className="form-label">Display Price Override</label>
                  <input
                    type="text"
                    value={serviceDisplayPrice}
                    onChange={(e) => setServiceDisplayPrice(e.target.value)}
                    placeholder="e.g. ₹599 – ₹799 or blank"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                <label className="form-label">Short Description</label>
                <textarea
                  rows={2}
                  value={serviceDescription}
                  onChange={(e) => setServiceDescription(e.target.value)}
                  placeholder="e.g. Deep pore cleansing, herbal mask & gentle face massage"
                  className="form-input"
                  style={{ resize: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setIsServiceModalOpen(false)}
                  className="btn btn-outline"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-berry" style={{ flex: 1 }}>
                  {serviceToEdit ? 'Save Changes' : 'Create Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT BUNDLE MODAL ── */}
      {isBundleModalOpen && (
        <div className="modal-overlay" style={{ zIndex: 120000 }} onClick={() => setIsBundleModalOpen(false)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '460px', padding: '1.75rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
              <h3 style={{ margin: 0, color: 'var(--blishka-berry)', fontFamily: 'var(--font-serif)', fontSize: '1.2rem' }}>
                {bundleToEdit ? 'Edit Curated Bundle' : 'Add Curated Bundle'}
              </h3>
              <button
                type="button"
                onClick={() => setIsBundleModalOpen(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '1.25rem' }}>
              Combine multiple beauty services into high-value doorstep packages.
            </p>

            <form onSubmit={handleSaveBundle}>
              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label className="form-label">Bundle Name *</label>
                <input
                  type="text"
                  required
                  value={bundleName}
                  onChange={(e) => setBundleName(e.target.value)}
                  placeholder="e.g. Blishka Bridal Radiance"
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label className="form-label">Services Included (comma-separated) *</label>
                <textarea
                  rows={2}
                  required
                  value={bundleServicesRaw}
                  onChange={(e) => setBundleServicesRaw(e.target.value)}
                  placeholder="e.g. Glow Facial, Full Arms Wax, Eyebrow, Upper Lip"
                  className="form-input"
                  style={{ resize: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div className="form-group" style={{ flex: 1, margin: 0 }}>
                  <label className="form-label">Package Price (₹) *</label>
                  <input
                    type="number"
                    required
                    min={0}
                    value={bundlePrice}
                    onChange={(e) => setBundlePrice(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="e.g. 999"
                    className="form-input"
                  />
                </div>
                <div className="form-group" style={{ flex: 1, margin: 0 }}>
                  <label className="form-label">Original Value (₹) *</label>
                  <input
                    type="number"
                    required
                    min={0}
                    value={bundleOriginalValue}
                    onChange={(e) => setBundleOriginalValue(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="e.g. 1399"
                    className="form-input"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <div className="form-group" style={{ flex: 1, margin: 0 }}>
                  <label className="form-label">Icon Style</label>
                  <select
                    value={bundleIconType}
                    onChange={(e) => setBundleIconType(e.target.value as any)}
                    className="form-input"
                    style={{ background: '#ffffff' }}
                  >
                    <option value="flower">Flower</option>
                    <option value="sparkle">Sparkle</option>
                    <option value="lotus">Lotus</option>
                    <option value="leaf">Leaf</option>
                    <option value="heart">Heart</option>
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1, margin: 0 }}>
                  <label className="form-label">Badge Color</label>
                  <input
                    type="color"
                    value={bundleTagColor}
                    onChange={(e) => setBundleTagColor(e.target.value)}
                    style={{ width: '100%', height: '38px', borderRadius: '8px', border: '1px solid #cbd5e1', cursor: 'pointer' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setIsBundleModalOpen(false)}
                  className="btn btn-outline"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-berry" style={{ flex: 1 }}>
                  {bundleToEdit ? 'Save Bundle' : 'Create Bundle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT CATEGORY MODAL ── */}
      {isCategoryModalOpen && (
        <div className="modal-overlay" style={{ zIndex: 120000 }} onClick={() => setIsCategoryModalOpen(false)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '460px', padding: '1.75rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
              <h3 style={{ margin: 0, color: 'var(--blishka-berry)', fontFamily: 'var(--font-serif)', fontSize: '1.2rem' }}>
                {categoryToEdit ? 'Edit Category' : 'Add New Category'}
              </h3>
              <button
                type="button"
                onClick={() => setIsCategoryModalOpen(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '1.25rem' }}>
              Create a new salon department (e.g. Bleach & De-Tan, Bridal Glamour, Mehendi).
            </p>

            <form onSubmit={handleSaveCategory}>
              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label className="form-label">Category Title / Label *</label>
                <input
                  type="text"
                  required
                  value={categoryFormLabel}
                  onChange={(e) => {
                    setCategoryFormLabel(e.target.value);
                    if (!categoryToEdit) {
                      setCategoryFormSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
                    }
                  }}
                  placeholder="e.g. Bleach & De-Tan"
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label className="form-label">Category Tagline *</label>
                <input
                  type="text"
                  required
                  value={categoryFormTagline}
                  onChange={(e) => setCategoryFormTagline(e.target.value)}
                  placeholder="e.g. Herbal skin lightening & instant glow"
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label className="form-label">Category Identifier / Slug</label>
                <input
                  type="text"
                  value={categoryFormSlug}
                  onChange={(e) => setCategoryFormSlug(e.target.value)}
                  placeholder="e.g. bleach-detan"
                  className="form-input"
                  disabled={Boolean(categoryToEdit && (categoryToEdit.id === 'bundles' || categoryToEdit.id === 'all'))}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                <label className="form-label">Image URL / Path (Optional)</label>
                <input
                  type="text"
                  value={categoryFormImage}
                  onChange={(e) => setCategoryFormImage(e.target.value)}
                  placeholder="e.g. /category-facial.jpg or leave blank for gradient"
                  className="form-input"
                />
                <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.35rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>Quick preset images:</span>
                  {['/category-facial.jpg', '/category-cleanup.jpg', '/category-waxing.jpg', '/category-manicure.jpg', '/category-hairspa.jpg'].map((img) => (
                    <button
                      key={img}
                      type="button"
                      onClick={() => setCategoryFormImage(img)}
                      style={{
                        fontSize: '0.68rem',
                        padding: '0.1rem 0.35rem',
                        borderRadius: '4px',
                        border: '1px solid #cbd5e1',
                        background: categoryFormImage === img ? 'var(--blishka-berry)' : '#f8fafc',
                        color: categoryFormImage === img ? '#ffffff' : 'var(--color-text-main)',
                        cursor: 'pointer'
                      }}
                    >
                      {img.replace('/category-', '').replace('.jpg', '')}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setIsCategoryModalOpen(false)}
                  className="btn btn-outline"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-berry" style={{ flex: 1 }}>
                  {categoryToEdit ? 'Save Category' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRMATION MODAL ── */}
      {deleteTarget && (
        <div className="modal-overlay" style={{ zIndex: 130000 }} onClick={() => setDeleteTarget(null)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '400px', padding: '1.75rem', textAlign: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: '#fee2e2',
                color: '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}
            >
              <Trash2 size={24} />
            </div>
            <h3 style={{ margin: '0 0 0.5rem', color: 'var(--color-text-main)', fontSize: '1.15rem' }}>
              Delete {deleteTarget.type === 'service' ? 'Service' : deleteTarget.type === 'bundle' ? 'Bundle' : 'Category'}?
            </h3>
            <p style={{ fontSize: '0.84rem', color: 'var(--color-text-muted)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Are you sure you want to remove <strong>"{deleteTarget.name}"</strong>? It will no longer appear on the customer menu.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="btn btn-outline"
                style={{ flex: 1 }}
              >
                Keep Item
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="btn btn-berry"
                style={{ flex: 1, background: '#ef4444', borderColor: '#ef4444' }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RESET TO DEFAULTS CONFIRMATION MODAL ── */}
      {isResetCatalogOpen && (
        <div className="modal-overlay" style={{ zIndex: 130000 }} onClick={() => setIsResetCatalogOpen(false)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '420px', padding: '1.75rem', textAlign: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: '#fef3c7',
                color: '#b45309',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}
            >
              <RotateCcw size={24} />
            </div>
            <h3 style={{ margin: '0 0 0.5rem', color: 'var(--color-text-main)', fontSize: '1.15rem' }}>
              Reset Menu to Defaults?
            </h3>
            <p style={{ fontSize: '0.84rem', color: 'var(--color-text-muted)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              This will restore all services, categories, curated bundles, and prices back to original factory defaults. Any custom additions will be cleared.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setIsResetCatalogOpen(false)}
                className="btn btn-outline"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResetCatalog}
                className="btn btn-berry"
                style={{ flex: 1 }}
              >
                Reset Menu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── UNLOCK SUPER ADMIN MODAL ── */}
      {isUnlockSuperModalOpen && (
        <div className="modal-overlay" style={{ zIndex: 135000 }} onClick={() => setIsUnlockSuperModalOpen(false)}>
          <div
            className="booking-modal-card admin-pin-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '380px',
              textAlign: 'center',
              padding: '1.75rem',
              border: '1.5px solid rgba(212, 175, 55, 0.5)',
              boxShadow: '0 12px 36px rgba(0, 0, 0, 0.25)'
            }}
          >
            <button
              className="modal-close-btn"
              onClick={() => setIsUnlockSuperModalOpen(false)}
              aria-label="Close unlock prompt"
            >
              <X size={18} />
            </button>

            <div
              style={{
                width: '52px',
                height: '52px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.18) 0%, rgba(184, 134, 11, 0.28) 100%)',
                color: '#b8860b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                boxShadow: '0 6px 20px rgba(184, 134, 11, 0.2)'
              }}
            >
              <Crown size={26} />
            </div>

            <h3
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: '1.3rem',
                color: '#7d5a00',
                marginBottom: '0.35rem'
              }}
            >
              Unlock Super Admin
            </h3>

            <p style={{ fontSize: '0.83rem', color: '#7c686e', marginBottom: '1.25rem', lineHeight: 1.45 }}>
              Enter your Super Admin PIN (Default: 7994) to view Total GMV, Cash in Hand, UPI Collections, and Financial Reconciliation.
            </p>

            <form onSubmit={handleUnlockSuperAdmin}>
              <div style={{ position: 'relative', maxWidth: '220px', margin: '0 auto 1rem' }}>
                <input
                  type={showSuperPin ? 'text' : 'password'}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="••••"
                  autoFocus
                  value={superPinInput}
                  onChange={(e) => {
                    setSuperPinInput(e.target.value.replace(/\D/g, '').slice(0, 6));
                    if (superPinError) setSuperPinError('');
                  }}
                  style={{
                    width: '100%',
                    fontSize: '1.6rem',
                    letterSpacing: '0.35em',
                    textAlign: 'center',
                    padding: '0.6rem 2.2rem 0.6rem 1rem',
                    borderRadius: '10px',
                    border: superPinError ? '2px solid #d32f2f' : '1.5px solid rgba(212, 175, 55, 0.6)',
                    background: '#ffffff',
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    color: '#b8860b',
                    outline: 'none'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowSuperPin(!showSuperPin)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    color: '#9e858a',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {showSuperPin ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {superPinError && (
                <div
                  style={{
                    color: '#d32f2f',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    marginBottom: '0.85rem'
                  }}
                >
                  {superPinError}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button
                  type="submit"
                  className="btn"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '0.9rem',
                    background: 'linear-gradient(135deg, #b8860b 0%, #d4af37 100%)',
                    color: '#ffffff',
                    border: 'none',
                    fontWeight: 700
                  }}
                >
                  <span>Verify & Unlock Finances</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsUnlockSuperModalOpen(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#8b757b',
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    padding: '0.35rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MANUAL BOOKING CREATION MODAL (FOR CALL / INSTAGRAM / WHATSAPP REQUESTS) ── */}
      {isManualBookingOpen && (
        <div
          className="modal-overlay"
          style={{ zIndex: 130000 }}
          onClick={() => {
            if (!isSubmittingManual) {
              setIsManualBookingOpen(false);
              setManualSuccessRecord(null);
            }
          }}
        >
          <div
            className="manual-booking-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            {manualSuccessRecord ? (
              /* Success / WhatsApp Confirmation Screen */
              <div style={{ textAlign: 'center', padding: '1rem 0.5rem' }}>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'rgba(46, 125, 50, 0.12)',
                    color: '#2e7d32',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem'
                  }}
                >
                  <CheckCircle2 size={36} />
                </div>

                <h3 style={{ margin: '0 0 0.35rem', color: 'var(--color-text-main)', fontSize: '1.35rem', fontFamily: 'var(--font-serif)' }}>
                  Manual Booking Confirmed!
                </h3>
                <p style={{ fontSize: '0.86rem', color: 'var(--color-text-muted)', margin: '0 0 1.25rem' }}>
                  Appointment has been successfully recorded in the dispatch system and synced to Cloudflare D1.
                </p>

                {/* Booking summary box */}
                <div
                  style={{
                    background: '#fcf8f9',
                    border: '1px solid var(--border-soft)',
                    borderRadius: '12px',
                    padding: '1.15rem',
                    textAlign: 'left',
                    maxWidth: '460px',
                    margin: '0 auto 1.5rem',
                    fontSize: '0.84rem'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.45rem' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Booking Reference:</span>
                    <strong style={{ color: 'var(--blishka-berry)' }}>{manualSuccessRecord.bookingRef}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.45rem' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Doorstep Start OTP:</span>
                    <strong style={{ color: '#2e7d32', fontFamily: 'monospace', fontSize: '0.96rem' }}>
                      {manualSuccessRecord.startOtp}
                    </strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.45rem' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Customer:</span>
                    <strong>{manualSuccessRecord.clientName} (+91 {manualSuccessRecord.phone})</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.45rem' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Schedule:</span>
                    <strong>{manualSuccessRecord.preferredDate} ({manualSuccessRecord.preferredSlot})</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.45rem' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Assigned Staff:</span>
                    <strong>{manualSuccessRecord.servicePerson || 'To be assigned'}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed var(--border-soft)', paddingTop: '0.45rem', marginTop: '0.45rem' }}>
                    <span style={{ fontWeight: 700 }}>Total Payable:</span>
                    <strong style={{ fontSize: '1rem', color: 'var(--color-text-main)' }}>
                      ₹{manualSuccessRecord.totalPrice} ({manualSuccessRecord.paymentStatus || 'Pending Collection'})
                    </strong>
                  </div>
                </div>

                {/* WhatsApp button */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', maxWidth: '460px', margin: '0 auto' }}>
                  <button
                    type="button"
                    onClick={() => openWhatsAppConfirmation(manualSuccessRecord)}
                    className="btn btn-whatsapp"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      fontSize: '0.92rem',
                      fontWeight: 700
                    }}
                  >
                    <WhatsAppIcon size={18} />
                    <span>Send WhatsApp Confirmation to Customer</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setIsManualBookingOpen(false);
                      setManualSuccessRecord(null);
                    }}
                    className="btn btn-outline"
                    style={{ width: '100%', padding: '0.7rem' }}
                  >
                    Close & View in Live Orders
                  </button>
                </div>
              </div>
            ) : (
              /* Create Booking Form */
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, var(--blishka-berry) 0%, #4a0a1f 100%)',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PhoneCall size={18} />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, color: 'var(--blishka-berry)', fontFamily: 'var(--font-serif)', fontSize: '1.25rem' }}>
                        + Add Manual Booking
                      </h3>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsManualBookingOpen(false)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}
                  >
                    <X size={20} />
                  </button>
                </div>

                <p style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', margin: '0 0 1.25rem' }}>
                  Log appointments received via direct Phone Call, Instagram DM, or WhatsApp into the central dispatch board.
                </p>

                <form onSubmit={handleSaveManualBooking}>
                  {/* Channel / Source Selector */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label className="form-label" style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--color-text-main)', marginBottom: '0.35rem', display: 'block' }}>
                      Booking Received Via (Channel) *
                    </label>
                    <div className="channel-selector-grid">
                      {[
                        { id: 'call', label: 'Phone Call', icon: PhoneCall, cls: 'call' },
                        { id: 'instagram', label: 'Instagram DM', icon: InstagramIcon, cls: 'instagram' },
                        { id: 'whatsapp', label: 'WhatsApp', icon: WhatsAppIcon, cls: 'whatsapp' },
                        { id: 'walkin', label: 'Walk-in / Ref', icon: Users, cls: '' },
                        { id: 'web', label: 'Website Direct', icon: Layers, cls: '' }
                      ].map((item) => {
                        const Icon = item.icon;
                        const isSelected = manualSource === item.id;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => setManualSource(item.id as any)}
                            className={`channel-selector-btn ${isSelected ? `selected ${item.cls}` : ''}`}
                          >
                            <Icon size={14} />
                            <span>{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Customer Information (2-Column Grid) */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.85rem', marginBottom: '0.85rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Client Name *</label>
                      <input
                        type="text"
                        required
                        value={manualClientName}
                        onChange={(e) => setManualClientName(e.target.value)}
                        placeholder="e.g. Ritu Verma"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">10-Digit Mobile Number *</label>
                      <input
                        type="tel"
                        required
                        maxLength={10}
                        value={manualPhone}
                        onChange={(e) => setManualPhone(e.target.value.replace(/\D/g, ''))}
                        placeholder="e.g. 9876543210"
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Area, Address & Pincode */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '0.85rem', marginBottom: '0.85rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Service Area *</label>
                      <select
                        value={manualArea}
                        onChange={(e) => setManualArea(e.target.value)}
                        className="form-input"
                        style={{ background: '#ffffff' }}
                      >
                        <option value="Bhilai Central / Civic Center">Civic Center / Central</option>
                        <option value="Nehru Nagar">Nehru Nagar</option>
                        <option value="Smriti Nagar / Junwani">Smriti Nagar / Junwani</option>
                        <option value="Supela / Radhika Nagar">Supela / Radhika Nagar</option>
                        <option value="Durg City / Station Area">Durg City / Station Area</option>
                        <option value="Power House / Sector 1-10">Power House / Sector Areas</option>
                        <option value="Risali / Ruabandha">Risali / Ruabandha</option>
                        <option value="Padmanabhpur">Padmanabhpur</option>
                        <option value="Other Area (Bhilai-Durg)">Other Area</option>
                      </select>
                    </div>

                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Doorstep Address & Landmark *</label>
                      <input
                        type="text"
                        required
                        value={manualAddress}
                        onChange={(e) => setManualAddress(e.target.value)}
                        placeholder="House / Flat No., Street, Landmark"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Pincode</label>
                      <input
                        type="text"
                        maxLength={6}
                        value={manualPincode}
                        onChange={(e) => setManualPincode(e.target.value.replace(/\D/g, ''))}
                        placeholder="490006"
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Schedule Date & Slot */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginBottom: '0.85rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Appointment Date *</label>
                      <input
                        type="date"
                        required
                        value={manualDate}
                        onChange={(e) => setManualDate(e.target.value)}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Time Slot *</label>
                      <select
                        value={manualSlot}
                        onChange={(e) => setManualSlot(e.target.value)}
                        className="form-input"
                        style={{ background: '#ffffff' }}
                      >
                        <option value="10:00 AM - 12:00 PM">10:00 AM - 12:00 PM (Morning)</option>
                        <option value="12:00 PM - 2:00 PM">12:00 PM - 2:00 PM (Afternoon)</option>
                        <option value="2:00 PM - 4:00 PM">2:00 PM - 4:00 PM (Mid-Day)</option>
                        <option value="4:00 PM - 6:00 PM">4:00 PM - 6:00 PM (Evening)</option>
                        <option value="06:00 PM - 07:30 PM">06:00 PM - 07:30 PM (Sunset)</option>
                        <option value="Immediate / Flexible">Immediate / Flexible Slot</option>
                      </select>
                    </div>
                  </div>

                  {/* Services Selection Box */}
                  <div style={{ background: '#faf5f7', border: '1px solid rgba(125, 26, 56, 0.15)', borderRadius: '12px', padding: '0.95rem', marginBottom: '0.85rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <label className="form-label" style={{ margin: 0, fontWeight: 700, color: 'var(--blishka-berry)' }}>
                        Select Services / Packages ({manualSelectedServices.length} selected) *
                      </label>
                      <span style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)' }}>Click any chip to add</span>
                    </div>

                    {/* Catalog search and quick chips */}
                    <div style={{ marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        value={manualServiceSearch}
                        onChange={(e) => setManualServiceSearch(e.target.value)}
                        placeholder="Search menu (e.g. Eyebrow, Facial, Wax, Bundle)..."
                        className="form-input"
                        style={{ height: '34px', fontSize: '0.78rem', background: '#ffffff' }}
                      />
                    </div>

                    {/* Quick service selector pills */}
                    <div style={{ display: 'flex', gap: '0.35rem', overflowX: 'auto', paddingBottom: '0.45rem', marginBottom: '0.65rem' }}>
                      {/* Bundles */}
                      {bundlesList.slice(0, 3).map((b) => (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => handleAddServiceToManual({ id: b.id, name: b.name, price: b.price, originalPrice: b.originalValue, category: 'bundle' })}
                          className="manual-service-chip"
                          style={{ borderColor: 'var(--blishka-berry)', color: 'var(--blishka-berry)' }}
                        >
                          <Gift size={11} />
                          <span>{b.name}</span>
                          <strong>₹{b.price}</strong>
                        </button>
                      ))}

                      {/* Filtered Services */}
                      {servicesList
                        .filter((s) => !manualServiceSearch || s.name.toLowerCase().includes(manualServiceSearch.toLowerCase()) || s.category.toLowerCase().includes(manualServiceSearch.toLowerCase()))
                        .slice(0, 8)
                        .map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => handleAddServiceToManual(s)}
                            className="manual-service-chip"
                          >
                            <Plus size={11} />
                            <span>{s.name}</span>
                            <strong>₹{s.price}</strong>
                          </button>
                        ))}
                    </div>

                    {/* Custom service row input */}
                    <div style={{ display: 'flex', gap: '0.45rem', alignItems: 'center', marginBottom: '0.65rem' }}>
                      <input
                        type="text"
                        value={customItemName}
                        onChange={(e) => setCustomItemName(e.target.value)}
                        placeholder="Or add custom offline service (e.g. Bridal Touchup)..."
                        className="form-input"
                        style={{ flex: 2, height: '34px', fontSize: '0.78rem', background: '#ffffff' }}
                      />
                      <input
                        type="number"
                        min={0}
                        value={customItemPrice}
                        onChange={(e) => setCustomItemPrice(e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder="Price (₹)"
                        className="form-input"
                        style={{ flex: 1, height: '34px', fontSize: '0.78rem', background: '#ffffff' }}
                      />
                      <button
                        type="button"
                        onClick={handleAddCustomServiceToManual}
                        className="btn btn-outline btn-sm"
                        style={{ height: '34px', fontSize: '0.76rem', padding: '0 0.65rem' }}
                      >
                        + Add Custom
                      </button>
                    </div>

                    {/* Selected services list */}
                    {manualSelectedServices.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '0.65rem', background: '#ffffff', borderRadius: '8px', border: '1px dashed #e2e8f0', color: 'var(--color-text-muted)', fontSize: '0.78rem' }}>
                        No services added yet. Click services above or add custom request.
                      </div>
                    ) : (
                      <div style={{ maxHeight: '140px', overflowY: 'auto' }}>
                        {manualSelectedServices.map((item, idx) => (
                          <div key={item.id} className="manual-selected-item-row">
                            <span style={{ fontWeight: 600 }}>{item.name}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                              <strong style={{ color: 'var(--blishka-berry)' }}>₹{item.price}</strong>
                              <button
                                type="button"
                                onClick={() => handleRemoveServiceFromManual(idx)}
                                style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '2px' }}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Billing breakdown */}
                    {manualSelectedServices.length > 0 && (
                      <div style={{ borderTop: '1px dashed rgba(125, 26, 56, 0.2)', paddingTop: '0.5rem', marginTop: '0.5rem', fontSize: '0.78rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                          <span style={{ color: 'var(--color-text-muted)' }}>Catalog Subtotal:</span>
                          <span>₹{manualCatalogPrice}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.2rem' }}>
                          <span style={{ color: 'var(--color-text-muted)' }}>Special Discount (₹):</span>
                          <input
                            type="number"
                            min={0}
                            value={manualDiscount}
                            onChange={(e) => setManualDiscount(e.target.value === '' ? '' : Number(e.target.value))}
                            placeholder="0"
                            style={{ width: '80px', height: '24px', textAlign: 'right', padding: '0 0.35rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.78rem' }}
                          />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem', color: '#2e7d32' }}>
                          <span>Doorstep Travel & Visit Fee:</span>
                          <span style={{ fontWeight: 700 }}>FREE (Waived)</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '0.9rem', color: 'var(--blishka-berry)', paddingTop: '0.3rem', borderTop: '1px solid rgba(125, 26, 56, 0.15)' }}>
                          <span>Final Amount Payable:</span>
                          <span>₹{manualFinalTotal}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Staff Assignment & Payment Status (2-Column Grid) */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.85rem', marginBottom: '0.85rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Assign Beautician (Optional)</label>
                      <select
                        value={manualAssignedStaff}
                        onChange={(e) => setManualAssignedStaff(e.target.value)}
                        className="form-input"
                        style={{ background: '#ffffff' }}
                      >
                        <option value="">-- To be assigned / Dispatch later --</option>
                        {staffList.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name} ({s.specialty}) · {s.isOnline ? 'Online' : 'Offline'}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Payment Status *</label>
                      <select
                        value={manualPaymentStatus}
                        onChange={(e) => setManualPaymentStatus(e.target.value as any)}
                        className="form-input"
                        style={{ background: '#ffffff' }}
                      >
                        <option value="Pending Collection">Pending Collection (Pay After Service)</option>
                        <option value="Collected via Cash">Collected via Cash</option>
                        <option value="Collected via UPI">Collected via UPI / Advance</option>
                      </select>
                    </div>
                  </div>

                  {/* Special Notes / Instagram Handle */}
                  <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                    <label className="form-label">Customer / Call / Instagram Notes</label>
                    <textarea
                      rows={2}
                      value={manualNotes}
                      onChange={(e) => setManualNotes(e.target.value)}
                      placeholder="e.g. IG username: @anita_roy, client has sensitive skin, requested Pooja if available."
                      className="form-input"
                      style={{ resize: 'none' }}
                    />
                  </div>

                  {/* Form Actions */}
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button
                      type="button"
                      onClick={() => setIsManualBookingOpen(false)}
                      disabled={isSubmittingManual}
                      className="btn btn-outline"
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmittingManual}
                      className="btn btn-berry"
                      style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem' }}
                    >
                      {isSubmittingManual ? (
                        <>
                          <RefreshCw size={15} className="spin-icon" />
                          <span>Dispatching Booking...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles size={16} />
                          <span>Create & Dispatch Booking</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ADD / EDIT OPERATIONAL AREA MODAL (SUPER ADMIN ONLY) ── */}
      {isAreaModalOpen && (
        <div className="modal-overlay" style={{ zIndex: 125000 }} onClick={() => setIsAreaModalOpen(false)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '460px', padding: '1.75rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <MapPin size={18} style={{ color: 'var(--blishka-berry)' }} />
                <h3 style={{ margin: 0, color: 'var(--blishka-berry)', fontFamily: 'var(--font-serif)', fontSize: '1.2rem' }}>
                  {areaToEdit ? 'Edit Operational Area' : 'Add Operational Area'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAreaModalOpen(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '1.25rem' }}>
              {areaToEdit
                ? 'Update colony name, regional city, or delivery serviceability.'
                : 'Define a new regional location/sector for Blishka doorstep beauty dispatches.'}
            </p>

            <form onSubmit={handleSaveArea}>
              <div className="form-group" style={{ marginBottom: '0.85rem' }}>
                <label className="form-label">Colony / Sector Name *</label>
                <input
                  type="text"
                  required
                  value={areaFormName}
                  onChange={(e) => setAreaFormName(e.target.value)}
                  placeholder="e.g. Junwani or Sector 11"
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '0.85rem' }}>
                <label className="form-label">Region / City</label>
                <input
                  type="text"
                  value={areaFormCity}
                  onChange={(e) => setAreaFormCity(e.target.value)}
                  placeholder="Bhilai"
                  className="form-input"
                />
              </div>

              {/* Serviceable Toggle */}
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid var(--border-soft)',
                  borderRadius: '10px',
                  padding: '0.75rem 0.85rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem'
                }}
              >
                <div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--color-text-main)' }}>
                    Instant Delivery Serviceable
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                    Customers in this area can checkout and book appointments
                  </div>
                </div>

                <label style={{ position: 'relative', display: 'inline-block', width: '42px', height: '24px', cursor: 'pointer', margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={areaFormIsServiceable}
                    onChange={(e) => setAreaFormIsServiceable(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: areaFormIsServiceable ? '#2e7d32' : '#cbd5e1',
                      borderRadius: '24px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        content: '""',
                        height: '18px',
                        width: '18px',
                        left: areaFormIsServiceable ? '20px' : '3px',
                        bottom: '3px',
                        backgroundColor: '#ffffff',
                        borderRadius: '50%',
                        transition: 'all 0.2s ease'
                      }}
                    />
                  </span>
                </label>
              </div>

              {/* GPS Coordinates (Optional) */}
              <div
                style={{
                  background: '#ffffff',
                  border: '1px dashed var(--border-soft)',
                  borderRadius: '10px',
                  padding: '0.85rem',
                  marginBottom: '1.25rem'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label className="form-label" style={{ margin: 0, fontSize: '0.78rem' }}>
                    GPS Coordinates (Optional)
                  </label>
                  <button
                    type="button"
                    onClick={handleDetectFormGps}
                    disabled={isDetectingAreaGps}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--blishka-berry)',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <Navigation size={12} className={isDetectingAreaGps ? 'spin-icon' : ''} />
                    <span>{isDetectingAreaGps ? 'Detecting...' : 'Detect Device GPS'}</span>
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '0.65rem' }}>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      value={areaFormLat}
                      onChange={(e) => setAreaFormLat(e.target.value)}
                      placeholder="Latitude e.g. 21.1960"
                      className="form-input"
                      style={{ fontSize: '0.78rem' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      value={areaFormLng}
                      onChange={(e) => setAreaFormLng(e.target.value)}
                      placeholder="Longitude e.g. 81.3320"
                      className="form-input"
                      style={{ fontSize: '0.78rem' }}
                    />
                  </div>
                </div>
                <small style={{ color: 'var(--color-text-muted)', fontSize: '0.7rem', display: 'block', marginTop: '0.35rem' }}>
                  Enables precision Haversine distance tracking against Hari Raj 5 km hub.
                </small>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setIsAreaModalOpen(false)}
                  className="btn btn-outline"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-berry" style={{ flex: 1 }}>
                  <span>{areaToEdit ? 'Save Changes' : 'Create Operational Area'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── DELETE AREA CONFIRMATION MODAL ── */}
      {areaToDelete && (
        <div className="modal-overlay" style={{ zIndex: 130000 }} onClick={() => setAreaToDelete(null)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '420px', padding: '1.75rem', textAlign: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: '#fee2e2',
                color: '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}
            >
              <Trash2 size={24} />
            </div>
            <h3 style={{ margin: '0 0 0.5rem', color: 'var(--color-text-main)', fontSize: '1.15rem' }}>
              Remove Operational Area?
            </h3>
            <p style={{ fontSize: '0.84rem', color: 'var(--color-text-muted)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Are you sure you want to remove <strong>"{areaToDelete.name}"</strong>? It will immediately disappear from customer location selection and address forms.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setAreaToDelete(null)}
                className="btn btn-outline"
                style={{ flex: 1 }}
              >
                Keep Area
              </button>
              <button
                type="button"
                onClick={handleDeleteAreaConfirm}
                className="btn btn-berry"
                style={{ flex: 1, background: '#ef4444', borderColor: '#ef4444' }}
              >
                Yes, Remove Area
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RESET OPERATIONAL AREAS CONFIRMATION MODAL ── */}
      {isResetAreasOpen && (
        <div className="modal-overlay" style={{ zIndex: 130000 }} onClick={() => setIsResetAreasOpen(false)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '420px', padding: '1.75rem', textAlign: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: '#fef3c7',
                color: '#b45309',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}
            >
              <RotateCcw size={24} />
            </div>
            <h3 style={{ margin: '0 0 0.5rem', color: 'var(--color-text-main)', fontSize: '1.15rem' }}>
              Reset Operational Areas?
            </h3>
            <p style={{ fontSize: '0.84rem', color: 'var(--color-text-muted)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              This will restore all 17 default Bhilai sectors and colonies back to original system defaults.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setIsResetAreasOpen(false)}
                className="btn btn-outline"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResetAreasConfirm}
                className="btn btn-berry"
                style={{ flex: 1 }}
              >
                Reset Areas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SUPER ADMIN: ADD CUSTOMER REVIEW MODAL ── */}
      {isAddReviewModalOpen && (
        <div className="modal-overlay" style={{ zIndex: 120000 }} onClick={() => setIsAddReviewModalOpen(false)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '480px', padding: '2rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
              <div>
                <h3 style={{ margin: 0, color: 'var(--blishka-berry)', fontWeight: 800 }}>
                  Add Customer Review
                </h3>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  Add a verified rating & review to feature on the Blishka homepage.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsAddReviewModalOpen(false)}
                className="cart-modal-close-btn"
                style={{ position: 'static' }}
              >
                <X size={18} />
              </button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!newRevName.trim() || !newRevComment.trim()) return;
                setIsSubmittingAdminReview(true);
                try {
                  const created = await submitReview({
                    customerName: newRevName.trim(),
                    rating: newRevRating,
                    reviewText: newRevComment.trim(),
                    serviceNames: newRevService.trim() || undefined,
                    area: newRevArea.trim() || 'Bhilai / Durg',
                    bookingRef: newRevBookingRef.trim() || undefined,
                    isVerified: true
                  }, adminPin);

                  setReviewsList([created, ...reviewsList.filter(r => r.id !== created.id)]);
                  showToast('Review added and published to homepage!');
                  setIsAddReviewModalOpen(false);
                  setNewRevName('');
                  setNewRevComment('');
                  setNewRevService('');
                  setNewRevRating(5);
                  setNewRevBookingRef('');
                } catch (err) {
                  showToast('Failed to add review');
                } finally {
                  setIsSubmittingAdminReview(false);
                }
              }}
            >
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.3rem' }}>
                  Customer Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Sneha Patel"
                  value={newRevName}
                  onChange={(e) => setNewRevName(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                />
              </div>

              {/* Star Rating Selector */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.3rem' }}>
                  Rating (1 to 5 Stars) *
                </label>
                <div style={{ display: 'flex', gap: '6px', cursor: 'pointer' }}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setNewRevRating(s)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px' }}
                    >
                      <Star
                        size={26}
                        fill={s <= newRevRating ? '#f59e0b' : 'none'}
                        color={s <= newRevRating ? '#f59e0b' : '#d1d5db'}
                      />
                    </button>
                  ))}
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#d97706', marginLeft: '0.5rem', alignSelf: 'center' }}>
                    {newRevRating} Star{newRevRating > 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.3rem' }}>
                  Review Text / Feedback *
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="e.g. Extremely polite beautician, arrived on time and did great waxing & facial!"
                  value={newRevComment}
                  onChange={(e) => setNewRevComment(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem', fontFamily: 'inherit' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.3rem' }}>
                    Services Availed
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Lotus Facial & Rica Wax"
                    value={newRevService}
                    onChange={(e) => setNewRevService(e.target.value)}
                    style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.3rem' }}>
                    Area / Locality
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Smriti Nagar, Bhilai"
                    value={newRevArea}
                    onChange={(e) => setNewRevArea(e.target.value)}
                    style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.3rem' }}>
                  Booking Ref (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. BL-98300"
                  value={newRevBookingRef}
                  onChange={(e) => setNewRevBookingRef(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setIsAddReviewModalOpen(false)}
                  className="btn btn-outline"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingAdminReview || !newRevName.trim() || !newRevComment.trim()}
                  className="btn btn-berry"
                  style={{ flex: 1, fontWeight: 700 }}
                >
                  {isSubmittingAdminReview ? 'Saving...' : 'Publish Review ★'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── SUPER ADMIN: DELETE REVIEW CONFIRMATION MODAL ── */}
      {reviewToDelete && (
        <div className="modal-overlay" style={{ zIndex: 120000 }} onClick={() => setReviewToDelete(null)}>
          <div
            className="booking-modal-card"
            style={{ maxWidth: '400px', padding: '1.75rem', textAlign: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ width: '48px', height: '48px', margin: '0 auto 1rem', borderRadius: '50%', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Trash2 size={24} />
            </div>
            <h4 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 800 }}>Delete Review?</h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: '0 0 1.25rem', lineHeight: 1.4 }}>
              Are you sure you want to remove the review from <strong>{reviewToDelete.customerName}</strong>? It will no longer appear on the homepage.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setReviewToDelete(null)}
                className="btn btn-outline"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const id = reviewToDelete.id;
                  setReviewToDelete(null);
                  const ok = await deleteReview(id, adminPin);
                  if (ok) {
                    setReviewsList(prev => prev.filter(r => r.id !== id));
                    showToast('Review deleted from homepage');
                  } else {
                    showToast('Failed to delete review');
                  }
                }}
                className="btn"
                style={{ flex: 1, background: '#dc2626', color: '#ffffff', fontWeight: 700 }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── LIVE TOAST NOTIFICATION ── */}
      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            background: 'var(--blishka-berry)',
            color: '#ffffff',
            padding: '0.75rem 1.25rem',
            borderRadius: '10px',
            fontSize: '0.85rem',
            fontWeight: 600,
            boxShadow: '0 8px 24px rgba(125, 26, 56, 0.35)',
            zIndex: 140000,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            animation: 'fadeInUp 0.25s ease'
          }}
        >
          <Check size={16} />
          <span>{toastMsg}</span>
        </div>
      )}
      {/* ── ADMIN INSTALL GUIDE MODAL ── */}
      <AdminInstallGuideModal
        isOpen={showAdminInstallModal}
        onClose={() => setShowAdminInstallModal(false)}
        role={currentRole}
      />

      {/* ── RESCHEDULE BOOKING MODAL (ADMIN & SUPER ADMIN MODE) ── */}
      <RescheduleBookingModal
        isOpen={!!adminRescheduleBooking}
        onClose={() => setAdminRescheduleBooking(null)}
        booking={adminRescheduleBooking}
        adminPin={adminPin || sessionStorage.getItem('blishka_admin_pin') || '7994'}
        onSuccess={handleAdminRescheduleSuccess}
      />
    </div>
  );
};
